import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import * as PIXI from "pixi.js";
import { Viewport } from "pixi-viewport";
// import { initDevtools } from "@pixi/devtools";
import { VideoTextureManager } from "../utils/videoTextureManager";
import spritePoolManager from "../utils/spritePoolManager";
import gsap from "gsap";
import { ANIMATION_FUNCTIONS } from "../utils/textStylePresets";
import { ensureFontLoaded } from "../utils/ensureFontLoaded";
import { isTypewriterCaptionStyle, sanitizeCaptionStyle, shouldUseWordContainer } from "../utils/captionStyleUtils";
import {
  moveElementForward,
  moveElementBackward,
  moveElementToFront,
  moveElementToBack,
  getSortedElements,
  getElementLayerPosition,
  canMoveForward,
  canMoveBackward,
  normalizeZIndices,
} from "../utils/layerUtils";

// Constants
const MAX_HISTORY_SIZE = 50;
const UNDO_REDO_DEBOUNCE_MS = 50; // Minimum time between undo/redo operations

const DEFAULT_CANVAS_BACKGROUND_COLOR = '#000000';

const normalizeHexColor = (color, fallback = DEFAULT_CANVAS_BACKGROUND_COLOR) => {
  if (typeof color !== 'string') return fallback;
  const hex = color.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex.toLowerCase();
  return fallback;
};

const parseHexColor = (value, fallback = 0x000000) => {
  if (typeof value !== 'string') return fallback;
  const normalized = normalizeHexColor(value, null);
  if (!normalized) return fallback;
  const parsed = parseInt(normalized.replace('#', ''), 16);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const drawSoftCircleCompat = (graphics, x, y, radius, color, alpha = 1) => {
  if (!graphics) return;
  const safeRadius = Math.max(0, Number(radius) || 0);
  if (safeRadius <= 0.25) return;

  const numericColor = Number(color);
  const safeColor = Number.isFinite(numericColor) ? numericColor : 0xffffff;
  const parsedAlpha = Number(alpha);
  const safeAlpha = Number.isFinite(parsedAlpha)
    ? Math.max(0, Math.min(1, parsedAlpha))
    : 1;
  if (safeAlpha <= 0.001) return;

  const safeX = Number(x) || 0;
  const safeY = Number(y) || 0;

  if (typeof graphics.circle === 'function' && typeof graphics.fill === 'function') {
    graphics.circle(safeX, safeY, safeRadius);
    graphics.fill({ color: safeColor, alpha: safeAlpha });
    return;
  }

  if (typeof graphics.beginFill === 'function' && typeof graphics.drawCircle === 'function') {
    graphics.beginFill(safeColor, safeAlpha);
    graphics.drawCircle(safeX, safeY, safeRadius);
    if (typeof graphics.endFill === 'function') graphics.endFill();
    return;
  }

  if (typeof graphics.drawCircle === 'function' && typeof graphics.fill === 'function') {
    graphics.drawCircle(safeX, safeY, safeRadius);
    graphics.fill({ color: safeColor, alpha: safeAlpha });
  }
};

const drawBlurSmudgeCompat = (graphics, x, y, radius, color, alpha = 1) => {
  if (!graphics) return;
  const baseRadius = Math.max(1, Number(radius) || 1);
  const parsedAlpha = Number(alpha);
  const baseAlpha = Number.isFinite(parsedAlpha)
    ? Math.max(0, Math.min(1, parsedAlpha))
    : 1;
  if (baseAlpha <= 0.001) return;

  const layers = [
    { scale: 2.2, alpha: 0.1 },
    { scale: 1.85, alpha: 0.14 },
    { scale: 1.5, alpha: 0.19 },
    { scale: 1.22, alpha: 0.25 },
    { scale: 0.96, alpha: 0.34 },
    { scale: 0.72, alpha: 0.46 },
    { scale: 0.54, alpha: 0.6 },
  ];

  layers.forEach((layer) => {
    drawSoftCircleCompat(
      graphics,
      x,
      y,
      baseRadius * layer.scale,
      color,
      baseAlpha * layer.alpha
    );
  });

  // Dense inner core so covered glyphs are fully obscured.
  drawSoftCircleCompat(graphics, x, y, baseRadius * 0.42, color, baseAlpha * 0.94);
};

// Properties that should be synced from layerProperties to PIXI sprites
// Add new properties here as needed - they'll automatically be handled everywhere
const PIXI_SYNC_PROPERTIES = [
  'alpha',
  'x',
  'y',
  'rotation',
  'scaleX',
  'scaleY',
  'volume',
  'muted',
  'width',
  'height',
  'anchorX',
  'anchorY',
];

// Check if a value is a plain JS object (not a class instance, DOM element, PIXI object, etc.)
const isPlainObject = (obj) => {
  if (!obj || typeof obj !== 'object') return false;
  const proto = Object.getPrototypeOf(obj);
  return proto === Object.prototype || proto === null;
};

// Deep merge utility for nested updates
const deepMerge = (target, source) => {
  const output = { ...target };

  for (const key in source) {
    if (
      isPlainObject(source[key]) &&
      key in target &&
      isPlainObject(target[key])
    ) {
      output[key] = deepMerge(target[key], source[key]);
    } else {
      output[key] = source[key];
    }
  }

  return output;
};

// Helper to create a clean snapshot of an element for history
const createElementSnapshot = (element) => {
  if (!element) return null;

  const { pixiSprite, captionSprite, ...cleanElement } = element;

  // Check if sprite is valid (not destroyed) - destroyed sprites have null scale
  const isSpriteValid = pixiSprite && pixiSprite.scale !== null && !pixiSprite.destroyed;

  // Store complete transform state
  const snapshot = {
    ...cleanElement,
    transform: isSpriteValid
      ? {
        x: pixiSprite.x || 0,
        y: pixiSprite.y || 0,
        rotation: pixiSprite.rotation || 0,
        scaleX: pixiSprite.scale?.x || 1,
        scaleY: pixiSprite.scale?.y || 1,
        width: pixiSprite.width || 0,
        height: pixiSprite.height || 0,
        anchorX: pixiSprite.anchor?.x || 0,
        anchorY: pixiSprite.anchor?.y || 0,
        visible: pixiSprite.visible !== undefined ? pixiSprite.visible : true,
        alpha: pixiSprite.alpha !== undefined ? pixiSprite.alpha : 1,
      }
      : null,
  };

  return snapshot;
};

// Helper to sync PIXI sprites with state - this ensures zIndex and interactive areas are updated
// whenever elements are added, updated, or deleted
const syncPixiSprites = (elements) => {
  elements.forEach((element) => {
    if (element.pixiSprite && !element.pixiSprite.destroyed && element.pixiSprite.scale !== null) {
      const zIndex = element.zIndex || 0;

      // Sync the main sprite (always in PIXI world space)
      element.pixiSprite.zIndex = zIndex;

      // Sync interactive area ONLY if it's a canvas interactive area (has PIXI parent)
      // Timeline interactive areas are React/DOM components and manage their own z-index
      if (element.pixiSprite.interactiveArea && element.pixiSprite.interactiveArea.parent) {
        element.pixiSprite.interactiveArea.zIndex = zIndex;

        // Mark interactive container as needing sort
        if (element.pixiSprite.interactiveArea.parent.sortableChildren) {
          element.pixiSprite.interactiveArea.parent.sortDirty = true;
        }
      }

      // Mark visual container as needing sort
      if (element.pixiSprite.parent && element.pixiSprite.parent.sortableChildren) {
        element.pixiSprite.parent.sortDirty = true;
      }
    }
  });
};

// Create default timeline data for new elements
const createDefaultTimeline = (
  componentData = null,
  duration = 10,
  currentTime
) => {
  const FPS = 25; // 25 frames per second
  console.log(
    "Creating default timeline with duration:",
    componentData?.duration,
    "and currentTime:",
    currentTime
  );

  // If componentData has frameDuration, use it
  if (componentData?.frameDuration) {
    const inPoint = Math.floor(currentTime * FPS) || 0;
    return {
      inPoint: inPoint,
      outPoint: inPoint + componentData.frameDuration, // End point is start + duration
      duration: componentData.frameDuration / FPS, // Convert to seconds
      layer: 0,
    };
  }

  // Fallback to default 10 second duration
  const inPoint = Math.floor(currentTime * FPS) || 0;
  const outPoint = componentData?.duration || 10; // 10 seconds default
  return {
    inPoint: inPoint,
    outPoint: inPoint + outPoint * FPS,
    duration: outPoint,
    layer: 0,
  };
};

const toggleMute = (element) => {
  if (element.pixiSprite && element.pixiSprite.videoElement) {
    const video = element.pixiSprite.videoElement;
    video.muted = !video.muted;
    // console.log(`Toggled mute for ${element.name || element.id}:`, video.muted);
    return video.muted;
  }
};

// Create the store
const useCanvasStore = create(
  subscribeWithSelector((set, get) => ({
    // Existing State
    projectName: "Untitled Project",
    selectedElement: null,
    selectedElements: [], // NEW: Array for multi-selection
    canvasElements: [],
    canvasSize: { width: 1920, height: 1080, name: 'Youtube Post (1920Ã—1080)' },
    canvasBackgroundColor: DEFAULT_CANVAS_BACKGROUND_COLOR,
    canvasZoom: 1,
    isPlaying: false,
    currentTime: 0,
    duration: 10,
    durationPadding: 3, // 3 seconds padding after highest outpoint
    isProjectLoading: false, // NEW: Flag to prevent saving during project load
    isElementReloadingFn: null, // Function to check if element is reloading
    expectedLayerCount: 0, // Track how many layers the project should have (prevents accidental layer deletion on save)
    projectLoadFailed: false, // Flag to indicate if project load had failures (prevents saving incomplete state)

    // Clipboard for copy/paste
    clipboard: [],

    addItemRequest: null,
    requestAddItem: (itemData) =>
      set({
        addItemRequest: { data: itemData, id: Date.now() },
      }),
    clearAddItemRequest: () => set({ addItemRequest: null }),

    // PIXI State
    pixiApp: null,
    viewport: null,
    visualContainer: null,
    pixiInitialized: false,
    containerRef: null,
    videoTextureManager: null,

    // History - start with initial empty state
    history: [
      {
        canvasElements: [],
        selectedElement: null,
        canvasBackgroundColor: DEFAULT_CANVAS_BACKGROUND_COLOR,
        timestamp: Date.now(),
      },
    ],
    historyIndex: 0,

    // Debounce tracking
    lastUndoRedoTime: 0,
    isRestoringHistory: false,

    // Job states - kept separate from history
    activeJobStates: new Map(),

    // PIXI Actions
    initializePixi: async (containerRef) => {
      const state = get();

      // Don't recreate if app already exists or container not ready
      if (!containerRef.current || state.pixiApp) return;

      console.log("Initializing canvas...");

      try {
        const rect = containerRef.current.getBoundingClientRect();

        // Create PIXI Application
        const app = new PIXI.Application();
        //Commenting out devtools for production build
        // initDevtools({ app });

        await app.init({
          width: rect.width,
          height: rect.height,
          backgroundColor: 0x141414,
          antialias: true,
          premultipliedAlpha: false,
          preserveDrawingBuffer: false,
          clearBeforeRender: true,
          powerPreference: "high-performance", // Request high-performance GPU
          hello: true, // Enable "Hello" message for debugging
          resolution: window.devicePixelRatio || 1, // Use device pixel ratio
          autoDensity: true,
          sharedTicker: true, // Use shared ticker for better performance
        });

        // Clear container and add canvas
        containerRef.current.innerHTML = "";
        containerRef.current.appendChild(app.canvas);

        // Create Viewport
        const newViewport = new Viewport({
          screenWidth: rect.width,
          screenHeight: rect.height,
          worldWidth: state.canvasSize.width,
          worldHeight: state.canvasSize.height,
          events: app.renderer.events,
        });

        // Add viewport to app stage
        app.stage.addChild(newViewport);

        // Configure viewport interactions
        // newViewport.drag({
        //   mouseButtons: ['middle', 'left'],
        //   keyToPress: 'Space',
        //   wheel: false,
        // });

        // newViewport.pinch().wheel();

        // Create containers
        const visualContainer = new PIXI.Container();
        const interactiveContainer = new PIXI.Container();
        // Enable sorting for layering
        visualContainer.sortableChildren = true;
        interactiveContainer.sortableChildren = true;

        // Add containers to viewport
        newViewport.addChild(visualContainer);
        newViewport.addChild(interactiveContainer);

        // Create scene background
        const scene = new PIXI.Graphics();
        scene.rect(0, 0, state.canvasSize.width, state.canvasSize.height);
        scene.fill(parseHexColor(state.canvasBackgroundColor, 0x000000));
        scene.stroke({ width: 1, color: 0x999999 });
        visualContainer.addChild(scene);

        // Create mask for visual container
        const worldMask = new PIXI.Graphics();
        worldMask.rect(0, 0, state.canvasSize.width, state.canvasSize.height);
        worldMask.fill(0xffffff);
        newViewport.addChild(worldMask);
        visualContainer.mask = worldMask;

        // Store container references on viewport for easy access
        newViewport.visualContainer = visualContainer;
        newViewport.interactiveContainer = interactiveContainer;

        // Initialize video texture manager
        const videoTextureManager = new VideoTextureManager(app);

        // Update store
        set({
          pixiApp: app,
          viewport: newViewport,
          visualContainer: visualContainer,
          pixiInitialized: true,
          containerRef,
          videoTextureManager,
        });

        // Center and fit viewport after initialization
        requestAnimationFrame(() => get().centerAndFitViewport(60));

        console.log("Canvas initialized successfully");
      } catch (error) {
        console.error("Failed to initialize Canvas:", error);
        set({ pixiInitialized: false });
      }
    },

    destroyPixi: () => {
      const state = get();
      if (state.pixiApp) {
        console.log("Cleaning up app...");

        // Destroy video texture manager
        if (state.videoTextureManager) {
          state.videoTextureManager.destroy();
        }

        // Remove playback ticker
        if (state.playbackTickerFn) {
          state.pixiApp.ticker.remove(state.playbackTickerFn);
        }

        state.pixiApp.destroy(true);
        set({
          pixiApp: null,
          viewport: null,
          visualContainer: null,
          pixiInitialized: false,
          containerRef: null,
          playbackTickerFn: null,
          videoTextureManager: null,
        });
      }
    },

    resizePixi: (newCanvasSize) => {
      const state = get();
      if (!state.pixiApp || !state.viewport || !state.containerRef?.current)
        return;

      console.log("Resizing canvas to:", newCanvasSize);

      // Get current container dimensions
      const rect = state.containerRef.current.getBoundingClientRect();

      // Resize the PIXI application renderer to container size
      state.pixiApp.renderer.resize(rect.width, rect.height);

      // Resize the viewport screen dimensions
      state.viewport.resize(rect.width, rect.height);

      // Update viewport world dimensions
      state.viewport.worldWidth = newCanvasSize.width;
      state.viewport.worldHeight = newCanvasSize.height;

      // Update the scene background to match new canvas size
      if (
        state.viewport.visualContainer &&
        state.viewport.visualContainer.children[0]
      ) {
        const scene = state.viewport.visualContainer.children[0]; // First child is the scene
        if (scene instanceof PIXI.Graphics) {
          scene.clear();
          scene.rect(0, 0, newCanvasSize.width, newCanvasSize.height);
          scene.fill(parseHexColor(state.canvasBackgroundColor, 0x000000));
          scene.stroke({ width: 1, color: 0x999999 });
        }
      }

      // Update the mask for visual container to match new canvas size
      if (
        state.viewport.visualContainer &&
        state.viewport.visualContainer.mask
      ) {
        const mask = state.viewport.visualContainer.mask;
        mask.clear();
        mask.rect(0, 0, newCanvasSize.width, newCanvasSize.height);
        mask.fill(0xffffff);
      }

      console.log("Renderer resolution:", state.pixiApp.renderer.resolution);
      console.log("DPR:", window.devicePixelRatio);

      // Re-center and fit the viewport after resize
      requestAnimationFrame(() => {
        get().centerAndFitViewport(60);

        // Auto-adjust captions position & wordWrap based on new canvas size
        const oldWidth = state.canvasSize.width;
        const oldHeight = state.canvasSize.height;

        state.canvasElements.forEach(element => {
          if (element.captionSprite && !element.captionSprite.destroyed) {
            // Update word wrap limit to fit new width
            if (element.captionSprite.style) {
              element.captionSprite.style.wordWrapWidth = newCanvasSize.width * 0.8;
            }

            // Proportionally remap from old canvas to new canvas
            const oldX = element.captionSprite.x || (oldWidth / 2);
            const oldY = element.captionSprite.y || (oldHeight * 0.85);
            const ratioX = oldX / oldWidth;
            const ratioY = oldY / oldHeight;
            let newX = ratioX * newCanvasSize.width;
            let newY = ratioY * newCanvasSize.height;

            // Clamp within bounds (anchor is 0.5)
            const halfW = element.captionSprite.width / 2;
            const halfH = element.captionSprite.height / 2;
            newX = Math.max(halfW, Math.min(newX, newCanvasSize.width - halfW));
            newY = Math.max(halfH, Math.min(newY, newCanvasSize.height - halfH));

            element.captionSprite.x = newX;
            element.captionSprite.y = newY;

            // Persist the remapped position to componentData
            if (element.componentData?.captions?.style) {
              element.componentData.captions.style.wordWrapWidth = newCanvasSize.width * 0.8;
              element.componentData.captions.style.x = newX;
              element.componentData.captions.style.y = newY;
            }
          }
        });
      });

      console.log("Canvas resized successfully");
    },

    centerAndFitViewport: (padding = 60) => {
      const state = get();
      if (!state.viewport || !state.containerRef?.current) {
        console.warn(
          "Cannot center viewport: viewport or container not available"
        );
        return;
      }

      const container = state.containerRef.current;
      const rectWidth = container.offsetWidth;
      const rectHeight = container.offsetHeight;

      // Ensure viewport is resized to current container
      state.viewport.resize(rectWidth, rectHeight);

      console.log("ðŸ“ Fitting canvas:", {
        container: { width: rectWidth, height: rectHeight },
        canvas: { width: state.canvasSize.width, height: state.canvasSize.height },
        padding,
        dpr: window.devicePixelRatio
      });

      // Calculate the uniform scale to fit canvas into container with padding
      // Preserve aspect ratio by taking the minimum scale factor
      const availableWidth = Math.max(0, rectWidth - (padding * 2));
      const availableHeight = Math.max(0, rectHeight - (padding * 2));
      const scaleX = availableWidth / state.canvasSize.width;
      const scaleY = availableHeight / state.canvasSize.height;
      const targetScale = Math.min(scaleX, scaleY, 1); // Min ensures fit; cap at 1 to prevent zoom-in

      // Apply uniform zoom
      state.viewport.setZoom(targetScale, true);

      // Center on canvas midpoint
      state.viewport.moveCenter(
        state.canvasSize.width / 2,
        state.canvasSize.height / 2
      );

      state.viewport.update(0);

      // Update store
      set(() => ({
        canvasZoom: targetScale,
      }));

      console.log(
        `âœ… Viewport fitted and centered with ${padding}px padding (scale: ${targetScale.toFixed(3)})`
      );
      console.log("ðŸŽ¯ Final viewport state:", {
        position: { x: state.viewport.x.toFixed(2), y: state.viewport.y.toFixed(2) },
        scale: { x: state.viewport.scale.x.toFixed(4), y: state.viewport.scale.y.toFixed(4) },
        center: state.viewport.center,
        effectiveAvailable: { width: availableWidth, height: availableHeight }
      });
    },

    // Existing Actions
    setProjectName: (name) => set({ projectName: name }),

    setIsProjectLoading: (isLoading) => set({ isProjectLoading: isLoading }),

    setExpectedLayerCount: (count) => set({ expectedLayerCount: count }),

    setProjectLoadFailed: (failed) => set({ projectLoadFailed: failed }),

    setIsElementReloadingFn: (fn) => set({ isElementReloadingFn: fn }),

    selectElement: (element) =>
      set(() => ({
        selectedElement: element,
        selectedElements: element ? [element] : [], // Single selection clears multi-select
      })),

    // NEW: Multi-selection actions
    selectElements: (elements) =>
      set(() => ({
        selectedElements: elements || [],
        selectedElement: elements && elements.length === 1 ? elements[0] : null,
      })),

    addToSelection: (element) =>
      set((state) => {
        const isAlreadySelected = state.selectedElements.some(
          (el) => el.id === element.id
        );
        if (isAlreadySelected) {
          // Remove from selection
          const newSelection = state.selectedElements.filter(
            (el) => el.id !== element.id
          );
          return {
            selectedElements: newSelection,
            selectedElement: newSelection.length === 1 ? newSelection[0] : null,
          };
        } else {
          // Add to selection
          const newSelection = [...state.selectedElements, element];
          return {
            selectedElements: newSelection,
            selectedElement: newSelection.length === 1 ? newSelection[0] : null,
          };
        }
      }),

    toggleElementSelection: (element) =>
      set((state) => {
        // Check if element is already in the selection
        const isAlreadySelected = state.selectedElements.some(
          (el) => el.id === element.id
        );

        if (isAlreadySelected) {
          // Remove from selection
          const newSelection = state.selectedElements.filter(
            (el) => el.id !== element.id
          );
          return {
            selectedElements: newSelection,
            selectedElement: newSelection.length === 1 ? newSelection[0] : null,
          };
        } else {
          // Add to selection (keep existing selections)
          const newSelection = [...state.selectedElements, element];
          return {
            selectedElements: newSelection,
            selectedElement: null, // Clear single selection when multi-selecting
          };
        }
      }),

    selectElementsInRange: (fromElement, toElement) =>
      set((state) => {
        const fromIndex = state.canvasElements.findIndex(
          (el) => el.id === fromElement.id
        );
        const toIndex = state.canvasElements.findIndex(
          (el) => el.id === toElement.id
        );

        if (fromIndex === -1 || toIndex === -1) return {};

        const startIndex = Math.min(fromIndex, toIndex);
        const endIndex = Math.max(fromIndex, toIndex);

        const newSelection = state.canvasElements.slice(
          startIndex,
          endIndex + 1
        );
        return {
          selectedElements: newSelection,
          selectedElement: newSelection.length === 1 ? newSelection[0] : null,
        };
      }),

    clearSelection: () =>
      set(() => ({
        selectedElement: null,
        selectedElements: [],
      })),

    isElementSelected: (elementId) => {
      const state = get();
      return (
        state.selectedElements.some((el) => el.id === elementId) ||
        state.selectedElement?.id === elementId
      );
    },

    zoomIn: (viewport) => {
      if (viewport) {
        const currentZoom = viewport.scaled;
        viewport.setZoom(currentZoom * 1.2, true);

        set(() => ({
          canvasZoom: currentZoom,
        }));
      }
    },

    zoomOut: (viewport) => {
      if (viewport) {
        const currentZoom = viewport.scaled;
        viewport.setZoom(currentZoom / 1.2, true);

        set(() => ({
          canvasZoom: currentZoom,
        }));
      }
    },

    setCanvasZoom: (zoom) => {
      const state = get();
      if (state.viewport) {
        state.centerAndFitViewport(zoom);
      }
      set(() => ({
        canvasZoom: zoom,
      }));
    },

    // Helper to save history AFTER making changes
    saveHistory: () => {
      const state = get();

      // Create snapshot of current state AFTER the change
      const snapshot = {
        canvasElements: state.canvasElements.map(createElementSnapshot),
        selectedElement: createElementSnapshot(state.selectedElement),
        selectedElementIds: state.selectedElements.map((el) => el.id), // Store IDs of selected elements
        canvasBackgroundColor: state.canvasBackgroundColor,
        timestamp: Date.now(),
      };

      // Remove any history after current index (for when we're not at the end)
      let newHistory = state.history.slice(0, state.historyIndex + 1);

      // Add the new snapshot
      newHistory.push(snapshot);

      // Limit history size
      if (newHistory.length > MAX_HISTORY_SIZE) {
        newHistory = newHistory.slice(1); // Remove oldest
      }

      set({
        history: newHistory,
        historyIndex: newHistory.length - 1,
      });
    },

    addElement: (element) => {
      set((state) => {
        // console.log("Adding new element to canvas:ðŸ˜ŽðŸ˜Ž", element);
        const maxZIndex =
          state.canvasElements.length > 0
            ? Math.max(...state.canvasElements.map((el) => el.zIndex || 0))
            : -1;

        const timeline =
          element.timeline ||
          createDefaultTimeline(
            element.componentData,
            state.duration,
            state.currentTime
          );
        //Added new values(layerProperties) for when we are reopening projects
        const newElement = {
          ...element,
          jobProgress: '',
          jobStatus: '',
          muted: element.layerProperties?.muted !== undefined
            ? element.layerProperties.muted
            : (element.muted !== undefined ? element.muted : false),
          volume: element.layerProperties?.volume || 1,
          alpha: element.layerProperties?.alpha || 1,
          zIndex: element.layerProperties?.zIndex !== undefined ?
            element.layerProperties.zIndex : maxZIndex + 1,
          timeline: timeline,
          // ADD THIS: Default playback mode to 'pause'
          playbackMode: element.layerProperties?.playbackMode || 'loop',
        };
        if (newElement.pixiSprite) {
          newElement.pixiSprite.zIndex = newElement.zIndex;
          if (newElement.pixiSprite.interactiveArea) {
            newElement.pixiSprite.interactiveArea.zIndex = newElement.zIndex;
          }
        }
        delete newElement.layerProperties; // Clean up layerProperties after use

        const newElements = [...state.canvasElements, newElement];
        syncPixiSprites(newElements);

        return {
          canvasElements: newElements,
          selectedElement: newElement,
        };
      });

      // Sync layerProperties with PIXI sprite if they exist
      // Only sync properties that are in the PIXI_SYNC_PROPERTIES list to avoid undefined errors
      if (element.layerProperties && typeof element.layerProperties === 'object') {
        const syncUpdates = {};

        // Only include properties from the configured list that exist in layerProperties
        PIXI_SYNC_PROPERTIES.forEach(prop => {
          if (element.layerProperties[prop] !== undefined && element.layerProperties[prop] !== null) {
            syncUpdates[prop] = element.layerProperties[prop];
          }
        });

        // Only call updateElement if there are properties to sync
        if (Object.keys(syncUpdates).length > 0) {
          get().updateElement(element.id, syncUpdates, true); // Skip history since we'll save it below
        }
      }

      // Update sprites visibility based on current time
      get().updateSpritesForTime(get().currentTime);

      // Update duration based on new element
      get().updateDurationFromElements();

      // Update expectedLayerCount to match the new element count (user intentionally added)
      // Only update if not during project loading (project load sets its own expected count)
      if (!get().isProjectLoading) {
        set((state) => ({ expectedLayerCount: state.canvasElements.length }));
      }

      // Save history AFTER the change
      get().saveHistory();
    },

    // This updates the elemens and syncs with PIXI sprites if they exist
    updateElement: (id, updates, skipHistory = false) => {

      // Safety check: ensure updates is a valid object
      if (!updates || typeof updates !== 'object') {
        console.warn('updateElement called with invalid updates:', updates);
        return;
      }

      // Update persistent job states if job status/progress is being updated
      if (updates.jobStatus !== undefined || updates.jobProgress !== undefined) {
        const state = get();
        const newJobStates = new Map(state.activeJobStates);

        // Get existing job state or create new one
        const currentJobState = newJobStates.get(id) || {};
        const updatedJobState = {
          ...currentJobState,
          ...(updates.jobStatus !== undefined && { jobStatus: updates.jobStatus }),
          ...(updates.jobProgress !== undefined && { jobProgress: updates.jobProgress })
        };

        // Store or remove job state based on status
        if (updatedJobState.jobStatus && updatedJobState.jobStatus !== '' && updatedJobState.jobStatus !== 'finished') {
          newJobStates.set(id, updatedJobState);
        } else if (updatedJobState.jobStatus === 'finished' || updatedJobState.jobStatus === '') {
          // Clean up finished jobs after a delay to allow final visual updates
          setTimeout(() => {
            const state = get();
            const jobStates = new Map(state.activeJobStates);
            jobStates.delete(id);
            set({ activeJobStates: jobStates });
          }, 2000);
        }

        set({ activeJobStates: newJobStates });
      }

      set(state => {
        const newElements = state.canvasElements.map(el => {
          if (el.id === id) {
            let updatedElement;

            // Check if updates is a complete replacement or partial update
            if (updates.componentData && updates.id && updates.type) {
              // This looks like a complete element replacement
              updatedElement = { ...updates };
            } else {
              // This is a partial update - use deep merge to preserve nested data
              updatedElement = deepMerge(el, updates);
            }

            // Ensure the element has required properties
            if (!updatedElement.id) {
              updatedElement.id = el.id;
            }

            // Sync properties to PIXI sprite if it exists
            // If updates include a new pixiSprite (e.g., media replacement), use that directly
            // and skip syncing properties on the old sprite which may be destroyed
            const sprite = updates.pixiSprite || el.pixiSprite;
            if (sprite && !sprite.destroyed && sprite.scale !== null) {

              // Sync alpha/opacity
              if (updates.alpha !== undefined) {
                sprite.alpha = updates.alpha;
                if (sprite.interactiveArea) {
                  sprite.interactiveArea.alpha = updates.alpha;
                }
              }

              // Sync position
              if (updates.x !== undefined) {
                sprite.x = updates.x;
                if (sprite.interactiveArea) {
                  sprite.interactiveArea.x = updates.x;
                }
              }
              if (updates.y !== undefined) {
                sprite.y = updates.y;
                if (sprite.interactiveArea) {
                  sprite.interactiveArea.y = updates.y;
                }
              }

              // Sync rotation
              if (updates.rotation !== undefined) {
                sprite.rotation = updates.rotation;
                if (sprite.interactiveArea) {
                  sprite.interactiveArea.rotation = updates.rotation;
                }
              }

              // Sync scale
              if (updates.scaleX !== undefined) {
                sprite.scale.x = updates.scaleX;
                if (sprite.interactiveArea) {
                  sprite.interactiveArea.scale.x = updates.scaleX;
                }
              }
              if (updates.scaleY !== undefined) {
                sprite.scale.y = updates.scaleY;
                if (sprite.interactiveArea) {
                  sprite.interactiveArea.scale.y = updates.scaleY;
                }
              }

              // Adjust resolution if it's text to prevent blurriness
              if (sprite.text !== undefined && sprite.style !== undefined) {
                 const scaleX = updates.scaleX !== undefined ? updates.scaleX : sprite.scale.x;
                 const scaleY = updates.scaleY !== undefined ? updates.scaleY : sprite.scale.y;
                 const maxScale = Math.max(scaleX, scaleY);
                 sprite.resolution = Math.max(2, window.devicePixelRatio || 2) * maxScale;
              }

              // Sync volume for video elements
              if (updates.volume !== undefined && sprite.videoElement) {
                sprite.videoElement.volume = updates.muted ? 0 : updates.volume;
              }

              // Sync muted state for video elements
              if (updates.muted !== undefined && sprite.videoElement) {
                sprite.videoElement.muted = updates.muted;
                sprite.videoElement.volume = updates.muted
                  ? 0
                  : updatedElement.volume || 1;
              }

              // Sync text properties if componentData.fields is updated
              if (updates.componentData?.fields && sprite.text !== undefined) {
                const fields = updates.componentData.fields;

                // Find text field updates
                Object.keys(fields).forEach((fieldKey) => {
                  const baseFieldName = fieldKey.replace(
                    /_fontSize|_fontFamily|_color/g,
                    ""
                  );

                  if (baseFieldName.includes("textfield")) {
                    // Update text content
                    if (!fieldKey.includes("_")) {
                      sprite.text = fields[fieldKey] || "";
                    }
                    // Update font size
                    else if (fieldKey.includes("_fontSize")) {
                      sprite.style.fontSize = fields[fieldKey];
                    }
                    // Update font family
                    else if (fieldKey.includes("_fontFamily")) {
                      sprite.style.fontFamily = fields[fieldKey];
                    }
                    // Update color
                    else if (fieldKey.includes("_color")) {
                      sprite.style.fill = fields[fieldKey];
                    }
                  }
                });
              }

              // Sync standalone text element properties
              if (sprite.text !== undefined && sprite.style !== undefined) {
                if (updates.componentData?.fontFamily !== undefined) {
                  sprite.style.fontFamily = updates.componentData.fontFamily;
                }
                if (updates.componentData?.fontSize !== undefined) {
                  sprite.style.fontSize = updates.componentData.fontSize;
                  sprite.style.resolution = Math.max(2, window.devicePixelRatio || 2) * Math.max(sprite.scale.x, sprite.scale.y);
                }
                if (updates.componentData?.color !== undefined) {
                  sprite.style.fill = updates.componentData.color;
                }
                if (updates.componentData?.text !== undefined) {
                  sprite.text = updates.componentData.text;
                }
              }

              // Sync caption properties if it's a media element with captions
              if (updates.componentData?.captions !== undefined && el.captionSprite && !el.captionSprite.destroyed) {
                const captionUpdates = updates.componentData.captions;
                if (captionUpdates.style) {
                  const nextCaptionStyle = sanitizeCaptionStyle(captionUpdates.style);
                  if (el.captionSprite._isWordContainer) {
                    // For word containers, always sync from full style object so stale
                    // values from old presets cannot leak into the next preset.
                    el.captionSprite._highlightColor = nextCaptionStyle.highlightColor || null;
                    el.captionSprite._baseColor = nextCaptionStyle.color || '#ffffff';
                    el.captionSprite._displayMode = nextCaptionStyle.displayMode || 'karaoke';
                    el.captionSprite._highlightBg = nextCaptionStyle.highlightBg || null;
                    el.captionSprite._wordAnimation = nextCaptionStyle.wordAnimation ?? null;
                    el.captionSprite._pastPhraseColor = nextCaptionStyle.pastPhraseColor || '#ffffff';
                    el.captionSprite._captionStyle = nextCaptionStyle;
                    el.captionSprite._wordsSignature = null;

                    if (!nextCaptionStyle.highlightBg && el.captionSprite._bgGraphics) {
                      if (el.captionSprite._bgGraphics.parent) {
                        el.captionSprite._bgGraphics.parent.removeChild(el.captionSprite._bgGraphics);
                      }
                      el.captionSprite._bgGraphics.destroy();
                      el.captionSprite._bgGraphics = null;
                      el.captionSprite._lastBgKey = null;
                    }

                    if (!nextCaptionStyle.activeWordBlur && el.captionSprite._activeWordBlurGraphics) {
                      if (el.captionSprite._activeWordBlurGraphics.parent) {
                        el.captionSprite._activeWordBlurGraphics.parent.removeChild(el.captionSprite._activeWordBlurGraphics);
                      }
                      el.captionSprite._activeWordBlurGraphics.destroy();
                      el.captionSprite._activeWordBlurGraphics = null;
                      el.captionSprite._activeWordBlurTrail = [];
                      el.captionSprite._lastActiveWordBlurKey = null;
                      el.captionSprite._lastActiveWordBlurAnchor = null;
                      el.captionSprite._lastActiveWordBlurTickMs = null;
                      el.captionSprite._activeWordBlurRenderedAnchor = null;
                    }
                    // Temporarily disabled caption background graphics support.
                    // if (!nextCaptionStyle.backgroundColor && el.captionSprite._captionBgGraphics) {
                    //   if (el.captionSprite._captionBgGraphics.parent) {
                    //     el.captionSprite._captionBgGraphics.parent.removeChild(el.captionSprite._captionBgGraphics);
                    //   }
                    //   el.captionSprite._captionBgGraphics.destroy();
                    //   el.captionSprite._captionBgGraphics = null;
                    //   el.captionSprite._lastCaptionBgKey = null;
                    // }
                  } else if (el.captionSprite.style) {
                    // Standard PIXI.Text - sync style properties
                    if (nextCaptionStyle.fontFamily !== undefined) {
                      el.captionSprite.style.fontFamily = nextCaptionStyle.fontFamily;
                    }
                    if (nextCaptionStyle.fontSize !== undefined) {
                      el.captionSprite.style.fontSize = nextCaptionStyle.fontSize;
                      el.captionSprite.style.resolution = Math.max(2, window.devicePixelRatio || 2);
                    }
                    if (nextCaptionStyle.color !== undefined) {
                      el.captionSprite.style.fill = nextCaptionStyle.color;
                    }
                    if (nextCaptionStyle.stroke !== undefined) {
                      el.captionSprite.style.stroke = nextCaptionStyle.stroke;
                    }
                    if (nextCaptionStyle.strokeThickness !== undefined) {
                      el.captionSprite.style.strokeThickness = nextCaptionStyle.strokeThickness;
                    }
                    if (nextCaptionStyle.lineJoin !== undefined) {
                      el.captionSprite.style.lineJoin = nextCaptionStyle.lineJoin;
                    }

                    // Temporarily disabled shadow styling.
                    // if (nextCaptionStyle.dropShadow !== undefined) {
                    //   el.captionSprite.style.dropShadow = nextCaptionStyle.dropShadow;
                    // }
                    // if (nextCaptionStyle.dropShadowColor !== undefined) {
                    //   el.captionSprite.style.dropShadowColor = nextCaptionStyle.dropShadowColor;
                    // }
                    // if (nextCaptionStyle.dropShadowBlur !== undefined) {
                    //   el.captionSprite.style.dropShadowBlur = nextCaptionStyle.dropShadowBlur;
                    // }
                    // if (nextCaptionStyle.dropShadowDistance !== undefined) {
                    //   el.captionSprite.style.dropShadowDistance = nextCaptionStyle.dropShadowDistance;
                    // }
                    el.captionSprite.style.dropShadow = false;

                    // Temporarily disabled caption background graphics support.
                    // if (!nextCaptionStyle.backgroundColor && el.captionSprite._captionBgGraphics) {
                    //   if (el.captionSprite._captionBgGraphics.parent) {
                    //     el.captionSprite._captionBgGraphics.parent.removeChild(el.captionSprite._captionBgGraphics);
                    //   }
                    //   el.captionSprite._captionBgGraphics.destroy();
                    //   el.captionSprite._captionBgGraphics = null;
                    // }
                  }
                }
                if (captionUpdates.phrases && captionUpdates.phrases.length > 0 && !el.captionSprite._isWordContainer) {
                  el.captionSprite.text = captionUpdates.phrases[0].text;
                }
              }
              // Keep the correct pixiSprite reference
              updatedElement.pixiSprite = sprite;
            }

            return updatedElement;
          }
          return el;
        });

        // Update selected element if it's the one being updated
        let newSelectedElement = state.selectedElement;
        if (state.selectedElement?.id === id) {
          newSelectedElement = newElements.find((el) => el.id === id);
        }

        // Update selectedElements array if this element is in it
        const newSelectedElements = state.selectedElements.map((el) => {
          if (el.id === id) {
            return newElements.find((e) => e.id === id);
          }
          return el;
        });

        // Sync PIXI sprites if zIndex changed
        if (updates.zIndex !== undefined) {
          syncPixiSprites(newElements);
        }

        return {
          canvasElements: newElements,
          selectedElement: newSelectedElement,
          selectedElements: newSelectedElements,
        };
      });
      // Save history AFTER the change
      if (!skipHistory) {
        get().saveHistory();
      }
    },

    // NEW: Specific method for updating timeline properties
    updateElementTimeline: (id, timelineUpdates, skipHistory = false) => {
      set((state) => {
        const newElements = state.canvasElements.map((el) => {
          if (el.id === id) {
            const updatedElement = {
              ...el,
              timeline: deepMerge(
                el.timeline || createDefaultTimeline(),
                timelineUpdates
              ),
            };

            return updatedElement;
          }
          return el;
        });

        let newSelectedElement = state.selectedElement;
        if (state.selectedElement?.id === id) {
          newSelectedElement = {
            ...state.selectedElement,
            timeline: deepMerge(
              state.selectedElement.timeline || createDefaultTimeline(),
              timelineUpdates
            ),
          };
        }

        // Update selectedElements array if this element is in it
        const newSelectedElements = state.selectedElements.map((el) => {
          if (el.id === id) {
            return newElements.find((e) => e.id === id);
          }
          return el;
        });

        return {
          canvasElements: newElements,
          selectedElement: newSelectedElement,
          selectedElements: newSelectedElements,
        };
      });

      // Update duration if timeline was changed
      get().updateDurationFromElements();

      if (!skipHistory) {
        get().saveHistory();
      }
    },

    // NEW: Batch update timeline for multiple elements
    updateMultipleElementTimelines: (updates, skipHistory = false) => {
      set((state) => {
        const updateMap = new Map(
          updates.map((u) => [u.id, u.timelineUpdates])
        );

        const newElements = state.canvasElements.map((el) => {
          if (updateMap.has(el.id)) {
            const timelineUpdates = updateMap.get(el.id);
            const updatedElement = {
              ...el,
              timeline: deepMerge(
                el.timeline || createDefaultTimeline(),
                timelineUpdates
              ),
            };

            return updatedElement;
          }
          return el;
        });

        // Update selected element if it was updated
        let newSelectedElement = state.selectedElement;
        if (state.selectedElement && updateMap.has(state.selectedElement.id)) {
          const timelineUpdates = updateMap.get(state.selectedElement.id);
          newSelectedElement = {
            ...state.selectedElement,
            timeline: deepMerge(
              state.selectedElement.timeline || createDefaultTimeline(),
              timelineUpdates
            ),
          };
        }

        // Update selectedElements array
        const newSelectedElements = state.selectedElements.map((el) => {
          return newElements.find((e) => e.id === el.id) || el;
        });

        return {
          canvasElements: newElements,
          selectedElement: newSelectedElement,
          selectedElements: newSelectedElements,
        };
      });

      // Update duration if timeline was changed
      get().updateDurationFromElements();

      if (!skipHistory) {
        get().saveHistory();
      }
    },

    toggleMute: (element) => {
      // Check if element has a pixiSprite with videoElement or soundId
      if (!element.pixiSprite) {
        console.warn(`Element ${element.id} does not have a pixiSprite.`);
        return false;
      }

      let mutedState;

      // Handle video elements
      if (element.pixiSprite.videoElement) {
        const video = element.pixiSprite.videoElement;
        video.muted = !video.muted;
        mutedState = video.muted;
        console.log(
          `Toggled video mute for ${element.name || element.id}:`,
          video.muted
        );
      }
      // Handle audio elements with PIXI Sound
      else if (element.pixiSprite.soundId) {
        const { sound } = require("@pixi/sound");
        const soundId = element.pixiSprite.soundId;

        // Toggle the muted state (inverse of current)
        mutedState = !element.muted;

        // Update volume in PIXI Sound
        if (sound.exists(soundId)) {
          const pixiSound = sound.find(soundId);
          if (pixiSound) {
            pixiSound.volume = mutedState ? 0 : 1.0;
          }
        }

        // console.log(
        //   `Toggled audio mute for ${element.name || element.id}:`,
        //   mutedState
        // );
      } else {
        console.warn(
          `Element ${element.name} does not have a video or audio element to toggle mute.`
        );
        return false;
      }

      // Update the store state using deepMerge
      set((state) => {
        const newElements = state.canvasElements.map((el) => {
          if (el.id === element.id) {
            return deepMerge(el, { muted: mutedState });
          }
          return el;
        });

        let newSelectedElement = state.selectedElement;
        if (state.selectedElement?.id === element.id) {
          newSelectedElement = deepMerge(state.selectedElement, {
            muted: mutedState,
          });
        }

        return {
          canvasElements: newElements,
          selectedElement: newSelectedElement,
        };
      });

      get().saveHistory();
      return true; // Successfully toggled
    },

    deleteElement: (id) => {
      set((state) => {
        const element = state.canvasElements.find((el) => el.id === id);
        if (element?.pixiSprite) {
          const sprite = element.pixiSprite;

          // Clean up progress overlay if it exists (attached to sprite like interactiveArea)
          if (sprite.progressOverlay && sprite.progressOverlay.parent) {
            sprite.progressOverlay.parent.removeChild(sprite.progressOverlay);
            sprite.progressOverlay.destroy({ children: true, texture: true, baseTexture: true });
            sprite.progressOverlay = null;
          }

          // Cleanup video ticker and resources
          if (sprite._cleanupVideo) {
            sprite._cleanupVideo();
          }

          // Cleanup audio resources
          if (sprite._cleanupAudio) {
            sprite._cleanupAudio();
          }

          // Cleanup AI placeholder animation
          if (sprite._cleanupAnimation) {
            sprite._cleanupAnimation();
          }

          // Stop any playing videos before destruction
          if (sprite.texture?.baseTexture?.resource?.source) {
            const videoSource = sprite.texture.baseTexture.resource.source;
            if (videoSource.pause) {
              videoSource.pause();
            }
            // Remove video source to free memory
            if (videoSource.src) {
              videoSource.src = "";
            }
          }

          // Clear any event listeners
          sprite.removeAllListeners();

          // Remove from containers
          if (sprite.parent) {
            sprite.parent.removeChild(sprite);
          }

          // Handle interactive area
          if (sprite.interactiveArea) {
            sprite.interactiveArea.removeAllListeners();
            if (sprite.interactiveArea.parent) {
              sprite.interactiveArea.parent.removeChild(sprite.interactiveArea);
            }
            // Destroy interactive area
            sprite.interactiveArea.destroy({
              children: true,
              texture: true,
              baseTexture: true,
            });
            sprite.interactiveArea = null;
          }

          // Destroy the sprite
          const isGifSprite = sprite.isGif || element.type === 'gif';
          if (isGifSprite) {
            // GIF sprites share their source via PIXI.Assets.cache.
            // Calling destroy() can invalidate the shared source and break
            // other GIF sprites using the same GIF. Just stop & detach instead.
            if (sprite.playing) {
              try { sprite.stop(); } catch (e) { /* already stopped */ }
            }
          } else {
            sprite.destroy({
              children: true,
              texture: true,
              baseTexture: true,
            });
          }

          // Clear the reference
          element.pixiSprite = null;
        }

        const newElements = normalizeZIndices(
          state.canvasElements.filter((el) => el.id !== id)
        );

        const newSelectedElement =
          state.selectedElement?.id === id ? null : state.selectedElement;

        syncPixiSprites(newElements);

        return {
          canvasElements: newElements,
          selectedElement: newSelectedElement,
          selectedElements: state.selectedElements.filter((el) => el.id !== id),
        };
      });

      // Update expectedLayerCount to match the new element count (user intentionally deleted)
      set((state) => ({ expectedLayerCount: state.canvasElements.length }));

      // Update duration after deleting element
      get().updateDurationFromElements();

      // Save history AFTER the change
      get().saveHistory();
    },

    reorderElement: (draggedIds, newVisualIndex) => {
      let finalElements = [];

      set((state) => {
        const idsToMove = Array.isArray(draggedIds) ? draggedIds : [draggedIds];

        // 1. Create visually sorted array (highest zIndex = index 0)
        const visualElements = [...state.canvasElements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));

        // 2. Separate elements to move and elements to keep
        // Preserve the visual order of elements being moved
        const elementsToMove = [];
        const elementsToKeep = [];

        visualElements.forEach((el) => {
          if (idsToMove.includes(el.id)) {
            elementsToMove.push(el);
          } else {
            elementsToKeep.push(el);
          }
        });

        if (elementsToMove.length === 0) {
          console.warn("No elements to reorder");
          return {};
        }

        // 3. Clamp the insertion index to valid range
        // The newVisualIndex represents where the TOP element of the group should go
        const adjustedIndex = Math.max(0, Math.min(newVisualIndex, elementsToKeep.length));

        // 4. Insert all elements to move as a contiguous block at the target position
        // This maintains their relative order
        const reorderedElements = [
          ...elementsToKeep.slice(0, adjustedIndex),
          ...elementsToMove,
          ...elementsToKeep.slice(adjustedIndex)
        ];

        // 5. Reassign zIndex values (highest at index 0)
        finalElements = reorderedElements.map((el, index) => ({
          ...el,
          zIndex: reorderedElements.length - 1 - index,
        }));

        return { canvasElements: finalElements };
      });

      // Sync PIXI sprites after state update
      syncPixiSprites(finalElements);

      // Save history AFTER the change
      get().saveHistory();
    },


    // Move Sprite in pixels
    moveSelectedElements: (deltaX, deltaY) => {
      const { selectedElements, updateElement, saveHistory } = get();

      // Do nothing if no elements are selected
      if (selectedElements.length === 0) {
        return;
      }

      // We will update each element but skip the individual history save,
      // then save a single history state at the end for a clean undo.
      selectedElements.forEach((element) => {
        if (element) {
          const newX = (element.x || 0) + deltaX;
          const newY = (element.y || 0) + deltaY;

          // Call the existing updateElement function, which already syncs the PIXI sprite.
          // The 'true' argument tells it to SKIP saving an individual history step.
          updateElement(element.id, { x: newX, y: newY }, true);
        }
      });

      // Now, after all elements have been moved, save a single, consolidated
      // state to the history for a perfect undo action.
      saveHistory();
      console.log(
        `Moved ${selectedElements.length} element(s) by (${deltaX}, ${deltaY})`
      );
    },

    // Layer management
    sendForward: (elementId) => {
      set((state) => {
        const newElements = normalizeZIndices(
          moveElementForward(state.canvasElements, elementId)
        );
        syncPixiSprites(newElements);
        return { canvasElements: newElements };
      });

      // Save history AFTER the change
      get().saveHistory();
    },

    sendBackward: (elementId) => {
      set((state) => {
        const newElements = normalizeZIndices(
          moveElementBackward(state.canvasElements, elementId)
        );
        syncPixiSprites(newElements);
        return { canvasElements: newElements };
      });

      // Save history AFTER the change
      get().saveHistory();
    },

    bringToFront: (elementId) => {
      set((state) => {
        const newElements = normalizeZIndices(
          moveElementToFront(state.canvasElements, elementId)
        );
        syncPixiSprites(newElements);
        return { canvasElements: newElements };
      });

      // Save history AFTER the change
      get().saveHistory();
    },

    sendToBack: (elementId) => {
      set((state) => {
        const newElements = normalizeZIndices(
          moveElementToBack(state.canvasElements, elementId)
        );
        syncPixiSprites(newElements);
        return { canvasElements: newElements };
      });

      // Save history AFTER the change
      get().saveHistory();
    },

    // Canvas settings
    setCanvasSize: (size) => {
      // Ensure size has a name property
      const sizeWithName = {
        ...size,
        name: size.name || `Custom (${size.width}Ã—${size.height})`
      };
      set(() => ({ canvasSize: sizeWithName }));
      // Automatically resize PIXI when canvas size changes
      get().resizePixi(sizeWithName);
    },

    setCanvasBackgroundColor: (color, skipHistory = false) => {
      const normalizedColor = normalizeHexColor(color);
      const stateBeforeUpdate = get();

      if (stateBeforeUpdate.canvasBackgroundColor === normalizedColor) return;

      set(() => ({ canvasBackgroundColor: normalizedColor }));

      const state = get();
      const scene = state.viewport?.visualContainer?.children?.[0];
      if (scene instanceof PIXI.Graphics) {
        scene.clear();
        scene.rect(0, 0, state.canvasSize.width, state.canvasSize.height);
        scene.fill(parseHexColor(normalizedColor, 0x000000));
        scene.stroke({ width: 1, color: 0x999999 });
      }

      if (!skipHistory && !stateBeforeUpdate.isRestoringHistory) {
        get().saveHistory();
      }
    },

    setTimelineZoom: (zoom) =>
      set(() => ({
        zoom: zoom,
      })),

    // Timeline playback controls
    setCurrentTime: (time) => {
      const state = get();
      const clampedTime = Math.max(0, Math.min(time, state.duration));

      set(() => ({
        currentTime: clampedTime,
      }));

      // Update sprite visibility and video playback based on timeline
      state.updateSpritesForTime(clampedTime);
    },

    setIsPlaying: (playing) =>
      set(() => ({
        isPlaying: playing,
      })),

    // Playback control
    togglePlayback: () => {
      const state = get();

      const newIsPlaying = !state.isPlaying;

      set({ isPlaying: newIsPlaying });

      if (newIsPlaying) {
        // Ensure current frame is rendered before starting playback
        if (state.pixiApp) {
          // Force an immediate render of the current state
          state.pixiApp.renderer.render(state.pixiApp.stage);
        }
        // Start playback loop
        state.startPlaybackLoop();
      } else {
        // Stop playback loop
        state.stopPlaybackLoop();
      }
    },

    // Helper method to restart all videos and audio when looping
    restartAllVideos: () => {
      const state = get();
      const FPS = 25;

      state.canvasElements.forEach((element) => {
        // Handle video elements
        if (element.pixiSprite?.videoElement && element.timeline) {
          const video = element.pixiSprite.videoElement;
          video._playPending = false;
          const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
          const isVisibleAtStart = 0 >= element.timeline.inPoint;

          // Reset to cropped start point
          video.currentTime = croppedStartTime;
          video.volume = element.muted ? 0 : (element.volume || 1);

          // Play if visible and timeline is playing
          if (isVisibleAtStart && state.isPlaying) {
            video.play().catch((e) => console.warn("Video restart failed:", e));
          } else {
            video.pause();
          }
        }

        // Handle GIF elements
        if (element.pixiSprite?.isGif && element.timeline) {
          const sprite = element.pixiSprite;
          const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
          const isVisibleAtStart = 0 >= element.timeline.inPoint;
          const totalFrames = sprite.totalFrames || 1;
          const animationSpeed = sprite.animationSpeed || 1;

          // Calculate the frame corresponding to the cropped start time
          const gifFrameFloat = (croppedStartTime * FPS * animationSpeed) % totalFrames;
          const startFrame = Math.max(0, Math.min(Math.floor(gifFrameFloat), totalFrames - 1));

          // Reset to cropped start frame
          sprite.currentFrame = startFrame;

          // Play if visible and timeline is playing
          if (isVisibleAtStart && state.isPlaying) {
            if (!sprite.playing) {
              sprite.play();
            }
          } else {
            if (sprite.playing) {
              sprite.stop();
            }
          }
        }

        // Handle audio elements with PIXI Sound
        if (element.pixiSprite?.soundId && element.timeline) {
          const { sound } = require("@pixi/sound");
          const soundId = element.pixiSprite.soundId;
          const isVisibleAtStart = 0 >= element.timeline.inPoint;

          // Stop any existing playback
          if (sound.exists(soundId)) {
            sound.stop(soundId);
          }

          // Play if visible and timeline is playing
          if (isVisibleAtStart && state.isPlaying) {
            const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
            const pixiSound = sound.find(soundId);
            if (pixiSound && pixiSound.isLoaded) {
              try {
                const playPromise = pixiSound.play({
                  start: croppedStartTime,
                  volume: element.muted ? 0 : element.volume,
                  loop: false,
                });
                if (playPromise && playPromise.catch) {
                  playPromise.catch(e => console.warn("Audio play failed:", e));
                }
              } catch (e) {
                console.warn("Audio play error:", e);
              }
            }
          }
        }
      });
    },

    // Playback loop management
    playbackTickerFn: null,

    startPlaybackLoop: () => {
      const state = get();
      const FPS = 25;

      // Remove any existing ticker function
      if (state.playbackTickerFn && state.pixiApp) {
        state.pixiApp.ticker.remove(state.playbackTickerFn);
      }

      // Clean up ALL pending scrub operations before starting playback
      state.canvasElements.forEach(element => {
        if (element.pixiSprite?.videoElement) {
          const video = element.pixiSprite.videoElement;

          // Cancel any pending video frame callbacks
          if (video._cancelVideoFrameCallback) {
            video._cancelVideoFrameCallback();
            video._cancelVideoFrameCallback = null;
          }

          // Remove any pending seeked event listeners
          if (video._scrubSeekHandler) {
            video.removeEventListener('seeked', video._scrubSeekHandler);
            video._scrubSeekHandler = null;
          }

          // Clear all scrub state flags
          video._scrubSeeking = false;
          video._scrubTargetTime = undefined;
        }
      });

      // Ensure sprites are updated for current time before starting
      // Force seek to ensure videos are synced
      state.updateSpritesForTime(state.currentTime, false);

      // Force a render to display the initial frame
      if (state.pixiApp) {
        state.pixiApp.renderer.render(state.pixiApp.stage);
      }

      // Create new ticker function
      const tickerFn = (ticker) => {
        const state = get();

        if (!state.isPlaying) {
          return;
        }

        // Use PIXI ticker's deltaTime (in frames at 60fps)
        const timeIncrement = (ticker.deltaTime / 60) * ticker.speed;
        let newTime = state.currentTime + timeIncrement;

        // Calculate content duration (without padding)
        const contentDuration = Math.max(
          0,
          state.duration - state.durationPadding
        );

        // Check if we're looping back to start based on content duration
        const isLooping = newTime >= contentDuration;
        // Loop back to start if we reach the content end
        if (isLooping) {
          newTime = 0;
          // Restart all videos when looping
          setTimeout(() => state.restartAllVideos(), 0);
        }

        //Commented out code that pauses timeline based on playBackMode
        // //NEW: Check if any visible elements with 'pause' mode have reached their end
        // const currentFrame = Math.floor(newTime * FPS);
        // let shouldStopTimeline = false;

        // state.canvasElements.forEach((element) => {
        //   if (!element.timeline) return;

        //   const { inPoint, outPoint } = element.timeline;
        //   const isVisible = currentFrame >= inPoint && currentFrame <= outPoint;

        //   // Check if element is visible, has pause mode, and has reached its end
        //   if (isVisible && (element.playbackMode === 'pause' || !element.playbackMode)) {
        //     const elementStartFrame = inPoint;
        //     const relativeFrame = currentFrame - elementStartFrame;
        //     const relativeTime = relativeFrame / FPS;
        //     const croppedStartTime = (element.croppedInPoint || 0) / FPS;
        //     const videoTime = croppedStartTime + relativeTime;

        //     const originalDuration = element.componentData?.frameDuration
        //       ? element.componentData.frameDuration / FPS
        //       : 10;

        //     // If this element has reached its end, we should stop the timeline
        //     if (videoTime >= originalDuration) {
        //       shouldStopTimeline = true;
        //     }
        //   }
        // });

        // // NEW: If any pause-mode element reached its end, stop playback
        // if (shouldStopTimeline) {
        //   state.stopPlaybackLoop();
        //   // Set currentTime to 0 so next play starts from beginning
        //   state.setCurrentTime(0);
        //   return;
        // }

        // // Check if timeline should loop
        // const isLooping = newTime >= contentDuration;

        // if (isLooping) {
        //   // NEW: Check if all elements are in loop mode before looping timeline
        //   const allElementsLoop = state.canvasElements.every((element) =>
        //     element.playbackMode === 'loop'
        //   );

        //   if (allElementsLoop) {
        //     // All elements are set to loop, so loop the timeline
        //     newTime = 0;
        //     setTimeout(() => state.restartAllVideos(), 0);
        //   } else {
        //     // At least one element is set to pause, so stop and reset
        //     state.stopPlaybackLoop();
        //     state.setCurrentTime(0);
        //     return;
        //   }
        // }

        // Update current time (this will also update sprites)
        state.setCurrentTime(newTime);
      };

      // Add to PIXI ticker
      if (state.pixiApp) {
        state.pixiApp.ticker.add(tickerFn);
        set({ playbackTickerFn: tickerFn });
      }
    },

    stopPlaybackLoop: () => {
      const state = get();

      // Remove ticker function
      if (state.playbackTickerFn && state.pixiApp) {
        state.pixiApp.ticker.remove(state.playbackTickerFn);
        set({ playbackTickerFn: null });
      }
      set({ isPlaying: false });
      // Pause all video, audio, and GIF sprites
      state.canvasElements.forEach((element) => {
        if (element.pixiSprite?.videoElement) {
          const video = element.pixiSprite.videoElement;
          // Cancel any pending play before pausing to avoid
          // "play() request was interrupted by a call to pause()" errors
          if (video._playPending) {
            video._playPending = false;
          }
          video.pause();
        }
        // Handle GIF sprites
        if (element.pixiSprite?.isGif && element.pixiSprite.playing) {
          element.pixiSprite.stop();
        }
        // Handle PIXI Sound audio
        if (element.pixiSprite?.soundId) {
          const { sound } = require("@pixi/sound");
          const soundId = element.pixiSprite.soundId;

          // Try to get the sound instance
          let pixiSound = element.pixiSprite.pixiSound;
          if (!pixiSound) {
            pixiSound = sound.find(soundId);
          }

          // Pause the sound if it's playing
          if (pixiSound && pixiSound.isPlaying) {
            pixiSound.pause();
            // Clear timing info since we're pausing
            if (element.pixiSprite) {
              element.pixiSprite._audioStartTime = null;
              element.pixiSprite._audioStartRealTime = null;
            }
          }
        }
      });
    },

    // Update sprites based on current timeline position
    // When playing: PixiJS auto-updates video textures via autoUpdate=true
    // When scrubbing: We manually update texture after setting currentTime
    updateSpritesForTime: (time, forceSeek = false) => {
      const state = get();
      const FPS = 25;
      const currentFrame = Math.floor(time * FPS);
      const isElementReloading = state.isElementReloadingFn;

      state.canvasElements.forEach((element) => {
        if (!element.pixiSprite || !element.timeline) return;

        // Check if element is currently reloading
        const isReloading = isElementReloading && isElementReloading(element.id);

        const { inPoint, outPoint } = element.timeline;
        const isVisible = currentFrame >= inPoint && currentFrame <= outPoint;

        // CRITICAL FIX: Don't update visibility if element is reloading
        // The reload process manages visibility to prevent texture errors
        if (!isReloading) {
          element.pixiSprite.visible = isVisible;
          if (element.pixiSprite.interactiveArea) {
            element.pixiSprite.interactiveArea.visible = isVisible;
          }

          // Skip inline caption updates here - it's handled globally below
        }

        // Handle GIF sprites with manual updates
        if (element.pixiSprite.isGif && element.componentData) {
          const sprite = element.pixiSprite;

          if (isVisible) {
            // Get total frames in the GIF
            // Always use manual calculation to respect cropping
            if (sprite.playing) {
              sprite.stop();
            }

            // Calculate which frame to show based on timeline position
            const relativeFrame = Math.max(0, currentFrame - inPoint);
            const relativeTime = relativeFrame / FPS;

            // Account for cropped start time
            const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;

            // Adjust time by animation speed and convert to ms
            const animationSpeed = sprite.animationSpeed || 1;
            const gifTimeMs = (croppedStartTime + relativeTime) * 1000 * animationSpeed;

            // Get source frames to calculate correct frame based on actual delays
            // GifSprite in PixiJS 8 stores frames in the source
            const gifSource = sprite.source || sprite.texture?.source;
            const frames = gifSource?.frames;

            let gifFrame = 0;

            if (frames && Array.isArray(frames) && frames.length > 0) {
              // Calculate total duration from frames if not present
              // PixiJS GifSource has a duration property
              const totalDurationMs = gifSource.duration || frames[frames.length - 1].end || 0;

              if (totalDurationMs > 0) {
                // Loop time
                const loopedTimeMs = gifTimeMs % totalDurationMs;

                // Find correct frame using start/end times
                for (let i = 0; i < frames.length; i++) {
                  const frame = frames[i];
                  // Check if current time falls within this frame's duration
                  if (loopedTimeMs >= frame.start && loopedTimeMs < frame.end) {
                    gifFrame = i;
                    break;
                  }
                }
              }
            } else {
              // Fallback if no frame data available (shouldn't happen for valid GIFs)
              // Assume 30 FPS default (standard for smooth stickers)
              const fallbackFPS = 30;
              const totalFrames = sprite.totalFrames || 1;
              const gifFrameFloat = ((gifTimeMs / 1000) * fallbackFPS) % totalFrames;
              gifFrame = Math.max(0, Math.min(Math.floor(gifFrameFloat), totalFrames - 1));
            }

            sprite.currentFrame = gifFrame;
          } else {
            // Not visible - stop animation
            if (sprite.playing) {
              sprite.stop();
            }
            // Reset to cropped start frame when not visible
            if (element.pixiSprite._wasVisible) {
              const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
              const animationSpeed = sprite.animationSpeed || 1;
              const gifFrameFloat = (croppedStartTime * FPS * animationSpeed) % (sprite.totalFrames || 1);
              sprite.currentFrame = Math.floor(gifFrameFloat);
            }
          }

          element.pixiSprite._wasVisible = isVisible;
        }

        // Handle video sprites
        if (element.pixiSprite.videoElement && element.componentData) {
          // CRITICAL FIX: Skip all video handling if element is reloading
          // This prevents texture dimension errors during video source reload
          if (isReloading) {
            console.log(`â¸ï¸ Skipping video update for ${element.name} - video is reloading`);
            return;
          }

          const video = element.pixiSprite.videoElement;
          video.volume = element.muted ? 0 : (element.volume || 1);

          if (isVisible) {
            // Calculate video time based on timeline position
            const relativeFrame = currentFrame - inPoint;
            const relativeTime = relativeFrame / FPS;
            const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
            const originalVideoDuration = element.componentData.frameDuration
              ? element.componentData.frameDuration / FPS
              : video.duration || 10;

            // Use the actual video duration to prevent seeking past the real end
            // frameDuration/FPS can exceed video.duration due to encoding differences
            const actualVideoDuration = (video.duration && video.duration > 0)
              ? Math.min(originalVideoDuration, video.duration)
              : originalVideoDuration;

            const videoTime = croppedStartTime + relativeTime;
            const playbackMode = element.playbackMode || 'loop';

            // Calculate final video time with loop support
            // For pause mode, clamp slightly before the end to show the actual last frame
            // (seeking to exactly video.duration can show frame 0 in some browsers)
            const finalVideoTime = playbackMode === 'loop'
              ? videoTime % actualVideoDuration
              : Math.min(videoTime, actualVideoDuration - 0.04);

            // Determine if video should be playing
            const shouldBePlaying = state.isPlaying &&
              !(playbackMode === 'pause' && videoTime >= actualVideoDuration);
            const isCurrentlyPlaying = !video.paused;

            if (shouldBePlaying && !isCurrentlyPlaying) {
              // PREVENT TICKER SPAM: Only request play once
              if (!video._playPending) {
                video._playPending = true;

                // Cancel any pending video frame callbacks
                if (video._cancelVideoFrameCallback) {
                  video._cancelVideoFrameCallback();
                  video._cancelVideoFrameCallback = null;
                }

                // Remove any pending seeked event listeners
                if (video._scrubSeekHandler) {
                  video.removeEventListener('seeked', video._scrubSeekHandler);
                  video._scrubSeekHandler = null;
                }

                // Clear all scrub state flags
                video._scrubSeeking = false;
                video._scrubTargetTime = undefined;

                // Only seek if genuinely out of sync (ignore forceSeek to prevent buffer flush)
                if (Math.abs(video.currentTime - finalVideoTime) > 0.04) {
                  video.currentTime = finalVideoTime;
                }

                video.playbackRate = 1.0;

                // Wrap the play promise so we can manage the pending state
                const executePlay = () => {
                  video.play()
                    .then(() => {
                      video._playPending = false;
                    })
                    .catch(e => {
                      video._playPending = false;
                      if (e.name !== 'AbortError') {
                        console.warn(`Video play failed for ${element.id}:`, e);
                      }
                    });
                };

                // If the video is still resolving a seek from your scrub,
                // waiting for it to finish before playing prevents the pipeline from choking.
                if (video.seeking) {
                  const onSeekComplete = () => {
                    executePlay();
                    video.removeEventListener('seeked', onSeekComplete);
                  };
                  video.addEventListener('seeked', onSeekComplete);
                } else {
                  executePlay();
                }
              }
            } else if (!shouldBePlaying && isCurrentlyPlaying) {
              // Need to pause
              video.pause();
              video._playPending = false;
            } else if (!state.isPlaying) {
              video._playPending = false;
              // SCRUBBING MODE: Throttled seeking to prevent seek queue buildup
              if (Math.abs(video.currentTime - finalVideoTime) > 0.04) {
                video._scrubTargetTime = finalVideoTime;

                if (video._scrubSeeking) {
                  return; // Already seeking, will pick up new target when done
                }

                const performSeek = (targetTime) => {
                  video._scrubSeeking = true;

                  const onSeekComplete = () => {
                    // CHECK: If playback started while seeking, abort scrub logic
                    if (get().isPlaying) {
                      video._scrubSeeking = false;
                      video._scrubTargetTime = undefined;
                      // Clean up handlers
                      if (video._cancelVideoFrameCallback) {
                        video._cancelVideoFrameCallback();
                        video._cancelVideoFrameCallback = null;
                      }
                      if (video._scrubSeekHandler) {
                        video.removeEventListener('seeked', video._scrubSeekHandler);
                        video._scrubSeekHandler = null;
                      }
                      return; // IMPORTANT: Exit to prevent interference
                    }

                    // Only update texture if video is ready
                    // Check video dimensions to ensure it's loaded properly
                    if (element.pixiSprite?.texture?.source?.update) {
                      const video = element.pixiSprite.videoElement;
                      if (video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
                        element.pixiSprite.texture.source.update();
                      }
                    }

                    video._scrubSeeking = false;

                    // Continue seeking if target moved
                    if (video._scrubTargetTime !== undefined &&
                      Math.abs(video.currentTime - video._scrubTargetTime) > 0.04) {
                      const nextTarget = video._scrubTargetTime;
                      video._scrubTargetTime = undefined;
                      performSeek(nextTarget);
                    } else {
                      video._scrubTargetTime = undefined;
                    }
                  };

                  // Clean up previous handlers
                  if (video._scrubSeekHandler) {
                    if (video._cancelVideoFrameCallback) {
                      video._cancelVideoFrameCallback();
                      video._cancelVideoFrameCallback = null;
                    }
                    video.removeEventListener('seeked', video._scrubSeekHandler);
                    video._scrubSeekHandler = null;
                  }

                  if ('requestVideoFrameCallback' in video) {
                    const handle = video.requestVideoFrameCallback(onSeekComplete);
                    video._cancelVideoFrameCallback = () => video.cancelVideoFrameCallback(handle);
                    video._scrubSeekHandler = onSeekComplete;
                  } else {
                    video._scrubSeekHandler = () => {
                      onSeekComplete();
                      video.removeEventListener('seeked', video._scrubSeekHandler);
                    };
                    video.addEventListener('seeked', video._scrubSeekHandler);
                  }

                  video.currentTime = targetTime;
                };

                performSeek(finalVideoTime);
              }
            }
            // If shouldBePlaying && isCurrentlyPlaying: let it play, PixiJS auto-updates
          } else {
            // Not visible - pause and reset
            if (!video.paused) {
              video.pause();
            }
            if (element.pixiSprite._wasVisible) {
              const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
              video.currentTime = croppedStartTime;
            }
          }

          element.pixiSprite._wasVisible = isVisible;
        }

        // Handle audio sprites using PIXI Sound
        if (
          element.pixiSprite &&
          element.pixiSprite.soundId &&
          element.componentData
        ) {
          const { sound } = require("@pixi/sound");
          const soundId = element.pixiSprite.soundId;

          // Try to get the sound instance directly from the sprite
          let pixiSound = element.pixiSprite.pixiSound;

          // If not on sprite, try to find it
          if (!pixiSound) {
            pixiSound = sound.find(soundId);
          }

          // If still no sound, skip this element
          if (!pixiSound) {
            console.warn(`Sound ${soundId} not found, skipping`);
            return;
          }

          if (isVisible) {
            // Calculate the audio time based on timeline position
            const elementStartFrame = inPoint;
            const relativeFrame = currentFrame - elementStartFrame;
            const relativeTime = relativeFrame / FPS;

            // Calculate the cropped start time
            const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;

            // Get the original audio duration from component data or PIXI Sound
            // PIXI Sound duration might be null if not fully loaded, so prefer componentData
            let originalAudioDuration = 10; // Default fallback

            if (element.componentData.frameDuration) {
              originalAudioDuration = element.componentData.frameDuration / FPS;
            } else if (pixiSound) {
              try {
                // Try to get duration, but it might be null if not loaded
                const duration = pixiSound.duration;
                if (duration && duration > 0) {
                  originalAudioDuration = duration;
                }
              } catch (e) {
                // If duration access fails, use default
                console.log("Could not get audio duration, using default");
              }
            }

            // Calculate audio time: cropped start + relative time
            const audioTime = croppedStartTime + relativeTime;

            // Use modulo to loop the audio time within its original duration
            const loopedAudioTime = audioTime % originalAudioDuration;

            // Handle play/pause state changes for audio
            if (state.isPlaying) {
              // For audio sync with timeline, we need to constantly check if we're at the right position
              // PIXI Sound doesn't support seeking while playing, so we need to restart if out of sync

              const isPlaying = pixiSound.isPlaying;

              // Check if we need to restart due to timeline position mismatch
              // We store the last start time to check if we're still in sync
              const expectedStartTime = element.pixiSprite._audioStartTime;
              const expectedCurrentTime = element.pixiSprite._audioStartRealTime
                ? (Date.now() - element.pixiSprite._audioStartRealTime) / 1000 +
                expectedStartTime
                : loopedAudioTime;

              // If we're too far out of sync (more than 0.1 seconds), restart
              const needsRestart =
                !isPlaying ||
                Math.abs(expectedCurrentTime - loopedAudioTime) > 0.1;

              if (needsRestart) {
                // Stop current playback if playing
                if (isPlaying) {
                  pixiSound.pause();
                }

                // Play from the calculated position
                // console.log(
                //   `Starting/Restarting audio ${soundId} from time ${loopedAudioTime}, volume: ${element.muted ? 0 : 1.0
                //   }`
                // );

                // Store when we started playing for sync checking
                element.pixiSprite._audioStartTime = loopedAudioTime;
                element.pixiSprite._audioStartRealTime = Date.now();

                pixiSound.play({
                  start: loopedAudioTime,
                  volume: element.muted ? 0 : element.volume,
                  loop: false,
                });
              } else {
                // Just update volume if mute state changed while playing
                pixiSound.volume = element.muted ? 0 : element.volume;
              }
            } else {
              // Not playing - pause the sound
              if (pixiSound && pixiSound.isPlaying) {
                pixiSound.pause();
                // Clear timing info
                element.pixiSprite._audioStartTime = null;
                element.pixiSprite._audioStartRealTime = null;
              }

              // Store position for next play
              element.pixiSprite._nextStartTime = loopedAudioTime;
            }
          } else {
            // Element not visible - stop audio if playing
            if (pixiSound && pixiSound.isPlaying) {
              pixiSound.stop();
              element.pixiSprite._audioStartTime = null;
              element.pixiSprite._audioStartRealTime = null;
            }

          }

          // Track visibility state for audio
          element.pixiSprite._wasVisible = isVisible;
        }
      });

      // --- GLOBAL CAPTION PRIORITY LOGIC ---
      // 1. Find all elements that are currently visible and have captions
      const visibleCaptionsElements = state.canvasElements.filter(element => {
        if (!element.timeline || !element.captionSprite || !element.componentData?.captions?.phrases) return false;

        // Fast skip if reloading
        const isReloading = state.isElementReloadingFn && state.isElementReloadingFn(element.id);
        if (isReloading) return false;

        const { inPoint, outPoint } = element.timeline;
        return currentFrame >= inPoint && currentFrame <= outPoint;
      });

      // 2. Sort by zIndex descending (highest index = top visual layer)
      visibleCaptionsElements.sort((a, b) => {
        const diff = (b.zIndex || 0) - (a.zIndex || 0);
        if (diff !== 0) return diff;
        return b.id.localeCompare(a.id); // Descending ID tiebreaker to exactly match Timeline.js
      });

      // 3. Process the winning element (if any) and hide all others
      const winningElement = visibleCaptionsElements[0]; // The top-most visible element with captions

      state.canvasElements.forEach(element => {
        if (!element.captionSprite) return;

        if (winningElement && element.id === winningElement.id) {
          // Calculate time for the winning element
          const { inPoint } = element.timeline;
          const relativeFrame = currentFrame - inPoint;
          const relativeTime = relativeFrame / FPS;
          const croppedStartTime = (element.timeline.croppedInPoint || 0) / FPS;
          const currentTimeMs = (croppedStartTime + relativeTime) * 1000;

          const captionStyle = sanitizeCaptionStyle(element.componentData.captions?.style || {});
          const viewport = state.viewport;
          const defaultX = viewport ? viewport.worldWidth / 2 : 960;
          const defaultY = viewport ? viewport.worldHeight * 0.85 : 900;
          
          let targetX = captionStyle.x ?? defaultX;
          let targetY = captionStyle.y ?? defaultY;

          if (element.captionSprite._isWordContainer) {
            // ======== WORD CONTAINER MODE: supports karaoke, progressive, wordByWord, phraseHighlight, phraseStack ========
            const container = element.captionSprite;
            const cs = sanitizeCaptionStyle((captionStyle && Object.keys(captionStyle).length > 0)
              ? captionStyle
              : (container._captionStyle || {}));
            const baseColor = cs.color || '#ffffff';
            const highlightTextColor = cs.highlightColor || null;
            const highlightBoxColor = cs.highlightBg || null;
            const displayMode = cs.displayMode || 'karaoke';
            const parsedHighlightFromPhraseIndex = parseInt(cs.highlightFromPhraseIndex, 10);
            const highlightFromPhraseIndex = displayMode === 'phraseHighlight' && Number.isFinite(parsedHighlightFromPhraseIndex)
              ? parsedHighlightFromPhraseIndex
              : 0;
            const revealOpacityMode = cs.revealOpacityMode || null;
            const supportsRevealOpacity = (
              displayMode === 'phraseHighlight' ||
              displayMode === 'progressive' ||
              displayMode === 'phraseStack'
            );
            const parsedInactiveOpacity = Number(cs.inactiveOpacity);
            const inactiveOpacity = Number.isFinite(parsedInactiveOpacity)
              ? Math.max(0, Math.min(1, parsedInactiveOpacity))
              : 0.28;
            const wordAnim = cs.wordAnimation ?? null;
            const pastPhraseColor = cs.pastPhraseColor || '#ffffff';
            const activeWordBlurEnabled = Boolean(cs.activeWordBlur);
            const activeWordBlurColor = parseHexColor(
              cs.activeWordBlurColor,
              parseHexColor(baseColor, 0xffffff)
            );
            const parsedActiveWordBlurRadius = Number(cs.activeWordBlurRadius);
            const activeWordBlurRadius = Number.isFinite(parsedActiveWordBlurRadius)
              ? Math.max(8, parsedActiveWordBlurRadius)
              : Math.max(24, (cs.fontSize || 36) * 0.82);
            const parsedActiveWordBlurTrailMs = Number(cs.activeWordBlurTrailMs);
            const activeWordBlurTrailMs = Number.isFinite(parsedActiveWordBlurTrailMs)
              ? Math.max(80, Math.min(2200, parsedActiveWordBlurTrailMs))
              : 460;
            const parsedActiveWordBlurOpacity = Number(cs.activeWordBlurOpacity);
            const activeWordBlurOpacity = Number.isFinite(parsedActiveWordBlurOpacity)
              ? Math.max(0, Math.min(1, parsedActiveWordBlurOpacity))
              : 0.94;
            const parsedActiveWordBlurOffsetX = Number(cs.activeWordBlurOffsetX);
            const activeWordBlurOffsetX = Number.isFinite(parsedActiveWordBlurOffsetX)
              ? parsedActiveWordBlurOffsetX
              : 8;
            const parsedActiveWordBlurOffsetY = Number(cs.activeWordBlurOffsetY);
            const activeWordBlurOffsetY = Number.isFinite(parsedActiveWordBlurOffsetY)
              ? parsedActiveWordBlurOffsetY
              : 2;
            const parsedActiveWordBlurWordAlpha = Number(cs.activeWordBlurWordAlpha);
            const activeWordBlurWordAlpha = Number.isFinite(parsedActiveWordBlurWordAlpha)
              ? Math.max(0, Math.min(1, parsedActiveWordBlurWordAlpha))
              : 0;
            const parsedActiveWordBlurXRatio = Number(cs.activeWordBlurXRatio);
            const activeWordBlurXRatio = Number.isFinite(parsedActiveWordBlurXRatio)
              ? Math.max(0, Math.min(1.3, parsedActiveWordBlurXRatio))
              : 0.74;
            const parsedActiveWordBlurRevealMs = Number(cs.activeWordBlurRevealMs);
            const activeWordBlurRevealMs = Number.isFinite(parsedActiveWordBlurRevealMs)
              ? Math.max(50, Math.min(1600, parsedActiveWordBlurRevealMs))
              : 190;
            const phrases = element.componentData.captions.phrases;
            
            // Flatten all words from all phrases, tagging each with phraseIdx
            let allWords = [];
            phrases.forEach((p, phraseIdx) => {
              if (p.words && p.words.length > 0) {
                p.words.forEach(w => {
                  allWords.push({
                    ...w,
                    phraseIdx,
                    _globalIndex: allWords.length,
                  });
                });
              } else {
                // Fallback: split phrase text and derive per-word timing from phrase bounds.
                // This avoids making every word active simultaneously when word-level timing is absent.
                const phraseWords = (p.text || '').split(/\s+/).filter(Boolean);
                const phraseDuration = Math.max(1, (p.end || 0) - (p.start || 0));
                const perWordDuration = phraseDuration / Math.max(1, phraseWords.length);

                phraseWords.forEach((w, wordIdx) => {
                  allWords.push({
                    text: w,
                    start: (p.start || 0) + (wordIdx * perWordDuration),
                    end: (p.start || 0) + ((wordIdx + 1) * perWordDuration),
                    phraseIdx,
                    _globalIndex: allWords.length,
                  });
                });
              }
            });

            // Find current active phrase index
            const activePhraseIdx = phrases.findIndex(p => currentTimeMs >= p.start && currentTimeMs <= p.end);
            
            // Determine if this mode uses per-phrase layout (each phrase is laid out independently).
            // Karaoke should also use phrase layout so active chunks stay centered rather than
            // inheriting x-offset from hidden earlier words in a long transcript flow.
            const usesPhrasedLayout = (
              displayMode === 'phraseHighlight' ||
              displayMode === 'phraseStack' ||
              displayMode === 'karaoke'
            );

            // Build a signature to detect when we need to rebuild
            const wordsSignature = [
              allWords.map(w => w.text).join('|'),
              `dm:${displayMode}`,
              `font:${cs.fontFamily || 'Arial'}`,
              `size:${cs.fontSize || 36}`,
              `color:${baseColor}`,
              `stroke:${cs.stroke || ''}`,
              `strokeW:${cs.strokeThickness || 0}`,
            ].join('|');
            
            if (container._wordsSignature !== wordsSignature) {
              container._wordsSignature = wordsSignature;
              
              // Remove old children and stale graphics so a preset swap fully resets visuals.
              while (container.children.length > 0) {
                const child = container.children[0];
                container.removeChild(child);
                if (child.destroy) child.destroy();
              }
              container._bgGraphics = null;
              container._activeWordBlurGraphics = null;
              container._captionBgGraphics = null;
              container._lastBgKey = null;
              container._lastCaptionBgKey = null;
              container._activeWordBlurTrail = [];
              container._lastActiveWordBlurKey = null;
              container._lastActiveWordBlurAnchor = null;
              container._lastActiveWordBlurTickMs = null;
              container._activeWordBlurRenderedAnchor = null;
              
              // Create word sprites
              const fontSize = cs.fontSize || 36;
              const baseMaxWidth = viewport ? viewport.worldWidth * 0.8 : 1600;
              const maxStageWidth = viewport ? viewport.worldWidth * 0.95 : baseMaxWidth;
              const spacing = fontSize * 0.3;
              const lineHeight = fontSize * 1.4;
              
              const wordSprites = [];
              allWords.forEach(w => {
                const wordStyle = new PIXI.TextStyle({
                  fontFamily: cs.fontFamily || 'Arial',
                  fontSize,
                  fill: baseColor,
                  fontWeight: 'bold',
                  ...(cs.stroke ? { stroke: cs.stroke, strokeThickness: cs.strokeThickness || 0 } : {}),
                  ...(cs.lineJoin ? { lineJoin: cs.lineJoin } : {}),
                  // Temporarily disabled drop-shadow styling.
                });
                
                const wordText = new PIXI.Text({ text: w.text, style: wordStyle });
                wordText.anchor.set(0, 0);
                wordText._wordData = w;
                wordText._hasAnimated = false;
                wordSprites.push(wordText);
                container.addChild(wordText);
              });

              let maxWidth = Math.min(maxStageWidth, baseMaxWidth);
              
              if (usesPhrasedLayout) {
                // â”€â”€ Per-phrase layout: each phrase is centered independently â”€â”€
                // Group word indices by phrase
                const phraseGroupsMap = {};
                allWords.forEach((w, idx) => {
                  if (!phraseGroupsMap[w.phraseIdx]) phraseGroupsMap[w.phraseIdx] = [];
                  phraseGroupsMap[w.phraseIdx].push(idx);
                });
                const phraseKeys = Object.keys(phraseGroupsMap).map(Number).sort((a, b) => a - b);

                let globalY = 0;
                phraseKeys.forEach(pIdx => {
                  const indices = phraseGroupsMap[pIdx];
                  let currentX = 0;
                  // For phraseHighlight all phrases overlap at y=0; for phraseStack they stack
                  const phraseY = (displayMode === 'phraseStack') ? globalY : 0;

                  indices.forEach(idx => {
                    const ws = wordSprites[idx];
                    ws.x = currentX;
                    ws.y = phraseY;
                    currentX += ws.width + spacing;
                  });

                  const lineW = currentX - spacing;
                  // Center this phrase
                  const phraseOffset = (maxWidth - lineW) / 2;
                  indices.forEach(idx => { wordSprites[idx].x += phraseOffset; });

                  if (displayMode === 'phraseStack') globalY += lineHeight;
                });

                const totalHeight = (displayMode === 'phraseStack') ? (globalY || lineHeight) : lineHeight;
                container.pivot.set(maxWidth / 2, totalHeight / 2);
              } else {
                // â”€â”€ Flow layout: all words in one continuous flow (karaoke, progressive, wordByWord) â”€â”€
                let currentX = 0;
                let currentY = 0;
                let lineStartIdx = 0;
                const lines = [];

                wordSprites.forEach((ws, idx) => {
                  const wordWidth = ws.width;
                  if (currentX > 0 && currentX + wordWidth > maxWidth) {
                    lines.push({ start: lineStartIdx, end: idx - 1, width: currentX - spacing, y: currentY });
                    currentX = 0;
                    currentY += lineHeight;
                    lineStartIdx = idx;
                  }
                  ws.x = currentX;
                  ws.y = currentY;
                  currentX += wordWidth + spacing;
                });

                if (wordSprites.length > 0) {
                  lines.push({ start: lineStartIdx, end: wordSprites.length - 1, width: currentX - spacing, y: currentY });
                }

                const totalHeight = currentY + lineHeight;
                lines.forEach(line => {
                  const lineOffset = (maxWidth - line.width) / 2;
                  for (let i = line.start; i <= line.end; i++) {
                    wordSprites[i].x += lineOffset;
                  }
                });

                container.pivot.set(maxWidth / 2, totalHeight / 2);
              }
            }
            
            // Build a per-tick visible-word window so all presets keep a max of 5 visible words.
            const MAX_VISIBLE_WORDS = 5;
            const activeWordGlobalIndex = allWords.findIndex(
              (w) => currentTimeMs >= w.start && currentTimeMs <= w.end
            );
            const baseVisibleWordIndices = [];
            allWords.forEach((w, idx) => {
              let baseVisible = false;
              switch (displayMode) {
                case 'progressive':
                  baseVisible = currentTimeMs >= w.start;
                  break;
                case 'wordByWord':
                  // Force a single visible token even if source timings overlap.
                  baseVisible = idx === activeWordGlobalIndex;
                  break;
                case 'phraseHighlight':
                  baseVisible = activePhraseIdx >= 0 && w.phraseIdx === activePhraseIdx;
                  break;
                case 'phraseStack':
                  baseVisible = activePhraseIdx >= 0 && w.phraseIdx <= activePhraseIdx;
                  break;
                case 'karaoke':
                default:
                  baseVisible = activePhraseIdx >= 0 && w.phraseIdx === activePhraseIdx;
                  break;
              }
              if (baseVisible) baseVisibleWordIndices.push(idx);
            });

            let visibleWordIndices = baseVisibleWordIndices;
            if (baseVisibleWordIndices.length > MAX_VISIBLE_WORDS) {
              let focusWordIndex = activeWordGlobalIndex;
              if (!baseVisibleWordIndices.includes(focusWordIndex)) {
                focusWordIndex = (
                  displayMode === 'progressive' || displayMode === 'phraseStack'
                    ? baseVisibleWordIndices[baseVisibleWordIndices.length - 1]
                    : baseVisibleWordIndices[0]
                );
              }

              const focusPos = Math.max(0, baseVisibleWordIndices.indexOf(focusWordIndex));
              const startPos = Math.max(
                0,
                Math.min(focusPos - 2, baseVisibleWordIndices.length - MAX_VISIBLE_WORDS)
              );
              visibleWordIndices = baseVisibleWordIndices.slice(startPos, startPos + MAX_VISIBLE_WORDS);
            }
            const visibleWordIndexSet = new Set(visibleWordIndices);

            // â”€â”€ Per-tick update: visibility + dynamic relayout for visible window â”€â”€
            container.children.forEach((wordSprite) => {
              const wd = wordSprite._wordData;
              if (!wd) return;
              wordSprite.visible = visibleWordIndexSet.has(wd._globalIndex);
            });

            const visibleWordSprites = container.children
              .filter((ws) => ws._wordData && ws.visible)
              .sort((a, b) => (a._wordData._globalIndex || 0) - (b._wordData._globalIndex || 0));

            const fontSize = cs.fontSize || 36;
            const spacing = fontSize * 0.3;
            const lineHeight = fontSize * 1.4;
            const baseMaxWidth = viewport ? viewport.worldWidth * 0.8 : 1600;
            const maxStageWidth = viewport ? viewport.worldWidth * 0.95 : baseMaxWidth;
            const maxWidth = Math.min(maxStageWidth, baseMaxWidth);

            const layoutFlow = (sprites, startY = 0) => {
              if (!sprites || sprites.length === 0) return { nextY: startY };

              const lines = [];
              let currentLine = [];
              let currentLineWidth = 0;

              sprites.forEach((ws) => {
                const wordWidth = ws.width;
                const nextWidth = currentLine.length > 0
                  ? currentLineWidth + spacing + wordWidth
                  : wordWidth;

                if (currentLine.length > 0 && nextWidth > maxWidth) {
                  lines.push({ sprites: currentLine, width: currentLineWidth });
                  currentLine = [ws];
                  currentLineWidth = wordWidth;
                } else {
                  currentLine.push(ws);
                  currentLineWidth = nextWidth;
                }
              });

              if (currentLine.length > 0) {
                lines.push({ sprites: currentLine, width: currentLineWidth });
              }

              let cursorY = startY;
              lines.forEach((line) => {
                let cursorX = -line.width / 2;
                line.sprites.forEach((ws) => {
                  ws.x = cursorX;
                  ws.y = cursorY;
                  cursorX += ws.width + spacing;
                });
                cursorY += lineHeight;
              });

              return { nextY: cursorY };
            };

            if (displayMode === 'phraseStack') {
              const phraseGroupsMap = {};
              visibleWordSprites.forEach((ws) => {
                const phraseIdx = ws._wordData?.phraseIdx ?? 0;
                if (!phraseGroupsMap[phraseIdx]) phraseGroupsMap[phraseIdx] = [];
                phraseGroupsMap[phraseIdx].push(ws);
              });
              const phraseKeys = Object.keys(phraseGroupsMap).map(Number).sort((a, b) => a - b);
              let cursorY = 0;
              phraseKeys.forEach((phraseKey) => {
                const result = layoutFlow(phraseGroupsMap[phraseKey], cursorY);
                cursorY = result.nextY;
              });
            } else {
              layoutFlow(visibleWordSprites, 0);
            }

            // â”€â”€ Per-word color + animation â”€â”€
            container.children.forEach((wordSprite) => {
              const wd = wordSprite._wordData;
              if (!wd || !wordSprite.style) return;

              const shouldBeVisible = wordSprite.visible;

              if (shouldBeVisible) {
                let targetColor = baseColor;
                const isActiveWord = currentTimeMs >= wd.start && currentTimeMs <= wd.end;
                let targetAlpha = 1;
                if (displayMode === 'phraseStack') {
                  targetColor = (wd.phraseIdx < activePhraseIdx) ? pastPhraseColor : baseColor;
                } else if (
                  highlightTextColor &&
                  isActiveWord &&
                  wd.phraseIdx >= highlightFromPhraseIndex
                ) {
                  targetColor = highlightTextColor;
                }

                if (supportsRevealOpacity && revealOpacityMode === 'activeTrail') {
                  targetAlpha = currentTimeMs >= wd.start ? 1 : inactiveOpacity;
                }

                if (activeWordBlurEnabled && isActiveWord) {
                  targetAlpha = Math.min(targetAlpha, activeWordBlurWordAlpha);
                } else if (
                  activeWordBlurEnabled &&
                  Number.isFinite(wd.end) &&
                  currentTimeMs > wd.end
                ) {
                  const revealProgress = Math.max(
                    0,
                    Math.min(1, (currentTimeMs - wd.end) / activeWordBlurRevealMs)
                  );
                  const easedReveal = 1 - Math.pow(1 - revealProgress, 3);
                  const revealedAlpha = activeWordBlurWordAlpha + (
                    (1 - activeWordBlurWordAlpha) * easedReveal
                  );
                  targetAlpha = Math.min(targetAlpha, revealedAlpha);
                }

                if (wordSprite.style.fill !== targetColor) {
                  wordSprite.style.fill = targetColor;
                }
                if (Math.abs(wordSprite.alpha - targetAlpha) > 0.001) {
                  if (activeWordBlurEnabled) {
                    const nextAlpha = wordSprite.alpha + ((targetAlpha - wordSprite.alpha) * 0.22);
                    wordSprite.alpha = Math.abs(nextAlpha - targetAlpha) < 0.003
                      ? targetAlpha
                      : nextAlpha;
                  } else {
                    wordSprite.alpha = targetAlpha;
                  }
                }
              } else if (Math.abs(wordSprite.alpha - 1) > 0.001) {
                wordSprite.alpha = 1;
              }

              if (
                wordAnim &&
                wordAnim !== 'typewriter' &&
                shouldBeVisible &&
                !wordSprite._hasAnimated &&
                !activeWordBlurEnabled
              ) {
                wordSprite._hasAnimated = true;
                if (ANIMATION_FUNCTIONS[wordAnim]) {
                  wordSprite.alpha = 0;
                  if (wordSprite.scale) wordSprite.scale.set(1, 1);
                  ANIMATION_FUNCTIONS[wordAnim](wordSprite, true, wordSprite.x, wordSprite.y);
                }
              }
              if (!shouldBeVisible && wordSprite._hasAnimated) {
                wordSprite._hasAnimated = false;
                wordSprite.alpha = 1;
              }
            });

            // Keep visible words centered to avoid inheriting offsets from hidden words.
            let visibleWordBounds = null;
            if (visibleWordSprites.length > 0) {
              visibleWordBounds = visibleWordSprites.reduce((acc, ws) => {
                const minX = Math.min(acc.minX, ws.x);
                const minY = Math.min(acc.minY, ws.y);
                const maxX = Math.max(acc.maxX, ws.x + ws.width);
                const maxY = Math.max(acc.maxY, ws.y + ws.height);
                return { minX, minY, maxX, maxY };
              }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });

              container.pivot.set(
                (visibleWordBounds.minX + visibleWordBounds.maxX) / 2,
                (visibleWordBounds.minY + visibleWordBounds.maxY) / 2
              );
            }

            // Temporarily disabled caption background rendering.
            if (container._captionBgGraphics) {
              container._captionBgGraphics.clear();
              container._lastCaptionBgKey = null;
            }

            // Highlight background box behind active word(s)
            if (highlightBoxColor) {
              if (!container._bgGraphics) {
                container._bgGraphics = new PIXI.Graphics();
                const insertIndex = container._captionBgGraphics ? 1 : 0;
                container.addChildAt(container._bgGraphics, insertIndex);
              }

              let newActiveKey = '';
              const activeWordSprites = [];
              container.children.forEach(ws => {
                if (ws._wordData && ws.visible) {
                  const isActiveWord = currentTimeMs >= ws._wordData.start && currentTimeMs <= ws._wordData.end;
                  if (isActiveWord) {
                    activeWordSprites.push(ws);
                    newActiveKey += ws._wordData.text + '|' + ws.x + '|';
                  }
                }
              });

              if (container._lastBgKey !== newActiveKey) {
                container._lastBgKey = newActiveKey;
                container._bgGraphics.clear();
                activeWordSprites.forEach(ws => {
                  const pad = 8;
                  container._bgGraphics.roundRect(ws.x - pad, ws.y - 4, ws.width + pad * 2, ws.height + 8, 6);
                  container._bgGraphics.fill(parseHexColor(highlightBoxColor), 1);
                });
                if (activeWordSprites.length > 0) {
                  gsap.fromTo(container._bgGraphics, { alpha: 0.4 }, { alpha: 1, duration: 0.25, ease: 'power2.out' });
                  gsap.fromTo(container._bgGraphics.scale, { x: 0.92, y: 0.92 }, { x: 1, y: 1, duration: 0.2, ease: 'back.out(2)' });
                }
              }
            } else if (container._bgGraphics) {
              container._bgGraphics.clear();
              container._lastBgKey = null;
            }

            if (activeWordBlurEnabled) {
              if (!container._activeWordBlurGraphics) {
                container._activeWordBlurGraphics = new PIXI.Graphics();
                container.addChild(container._activeWordBlurGraphics);
              } else if (
                container._activeWordBlurGraphics.parent === container &&
                typeof container.getChildIndex === 'function' &&
                typeof container.setChildIndex === 'function'
              ) {
                const topIndex = Math.max(0, container.children.length - 1);
                if (container.getChildIndex(container._activeWordBlurGraphics) !== topIndex) {
                  container.setChildIndex(container._activeWordBlurGraphics, topIndex);
                }
              }

              if (!Array.isArray(container._activeWordBlurTrail)) {
                container._activeWordBlurTrail = [];
              }

              const previousTickMs = Number(container._lastActiveWordBlurTickMs);
              if (Number.isFinite(previousTickMs) && currentTimeMs < previousTickMs) {
                container._activeWordBlurTrail = [];
                container._lastActiveWordBlurKey = null;
                container._lastActiveWordBlurAnchor = null;
                container._activeWordBlurRenderedAnchor = null;
              }
              container._lastActiveWordBlurTickMs = currentTimeMs;

              const activeWordSprite = visibleWordSprites.find((ws) => (
                ws._wordData &&
                currentTimeMs >= ws._wordData.start &&
                currentTimeMs <= ws._wordData.end
              )) || null;

              const activeAnchor = activeWordSprite
                ? {
                  x: activeWordSprite.x + (activeWordSprite.width * activeWordBlurXRatio) + activeWordBlurOffsetX,
                  y: activeWordSprite.y + (activeWordSprite.height * 0.56) + activeWordBlurOffsetY,
                  radius: activeWordBlurRadius + (activeWordSprite.height * 0.04),
                }
                : null;
              const activeWordId = activeWordSprite
                ? (activeWordSprite._wordData?._globalIndex ?? -1)
                : null;

              const previousActiveKey = container._lastActiveWordBlurKey ?? null;
              const previousAnchor = container._lastActiveWordBlurAnchor || null;
              if (
                Number.isFinite(previousActiveKey) &&
                previousAnchor &&
                previousActiveKey !== activeWordId
              ) {
                container._activeWordBlurTrail.push({
                  ...previousAnchor,
                  startedAtMs: currentTimeMs,
                });
              }

              container._lastActiveWordBlurKey = activeWordId;
              container._lastActiveWordBlurAnchor = activeAnchor;

              let renderedAnchor = container._activeWordBlurRenderedAnchor || null;
              if (activeAnchor) {
                if (!renderedAnchor) {
                  renderedAnchor = { ...activeAnchor };
                } else {
                  const isWordSwitch = (
                    Number.isFinite(previousActiveKey) &&
                    previousActiveKey !== activeWordId
                  );
                  const posEase = isWordSwitch ? 0.24 : 0.14;
                  const radiusEase = isWordSwitch ? 0.2 : 0.12;
                  renderedAnchor.x += (activeAnchor.x - renderedAnchor.x) * posEase;
                  renderedAnchor.y += (activeAnchor.y - renderedAnchor.y) * posEase;
                  renderedAnchor.radius += (activeAnchor.radius - renderedAnchor.radius) * radiusEase;
                }
                container._activeWordBlurRenderedAnchor = renderedAnchor;
              } else {
                container._activeWordBlurRenderedAnchor = null;
                renderedAnchor = null;
              }

              container._activeWordBlurTrail = container._activeWordBlurTrail
                .map((trail) => ({
                  ...trail,
                  ageMs: currentTimeMs - (Number(trail.startedAtMs) || currentTimeMs),
                }))
                .filter((trail) => trail.ageMs >= 0 && trail.ageMs <= activeWordBlurTrailMs);

              container._activeWordBlurGraphics.clear();
              container._activeWordBlurTrail.forEach((trail) => {
                const fade = 1 - (trail.ageMs / activeWordBlurTrailMs);
                const easedFade = Math.sqrt(Math.max(0, fade));
                drawBlurSmudgeCompat(
                  container._activeWordBlurGraphics,
                  trail.x,
                  trail.y,
                  trail.radius * (1 + ((1 - easedFade) * 0.2)),
                  activeWordBlurColor,
                  activeWordBlurOpacity * easedFade * 0.72
                );
              });

              if (renderedAnchor) {
                drawBlurSmudgeCompat(
                  container._activeWordBlurGraphics,
                  renderedAnchor.x,
                  renderedAnchor.y,
                  renderedAnchor.radius,
                  activeWordBlurColor,
                  activeWordBlurOpacity
                );
              }
            } else if (container._activeWordBlurGraphics) {
              if (container._activeWordBlurGraphics.parent) {
                container._activeWordBlurGraphics.parent.removeChild(container._activeWordBlurGraphics);
              }
              container._activeWordBlurGraphics.destroy();
              container._activeWordBlurGraphics = null;
              container._activeWordBlurTrail = [];
              container._lastActiveWordBlurKey = null;
              container._lastActiveWordBlurAnchor = null;
              container._lastActiveWordBlurTickMs = null;
              container._activeWordBlurRenderedAnchor = null;
            }
            // Position and visibility
            if (viewport && container) {
              if (visibleWordBounds) {
                const leftExtent = Math.max(1, container.pivot.x - visibleWordBounds.minX);
                const rightExtent = Math.max(1, visibleWordBounds.maxX - container.pivot.x);
                const topExtent = Math.max(1, container.pivot.y - visibleWordBounds.minY);
                const bottomExtent = Math.max(1, visibleWordBounds.maxY - container.pivot.y);

                targetX = Math.max(leftExtent, Math.min(targetX, viewport.worldWidth - rightExtent));
                targetY = Math.max(topExtent, Math.min(targetY, viewport.worldHeight - bottomExtent));
              } else {
                const halfW = container.width / 2;
                const halfH = container.height / 2;
                targetX = Math.max(halfW, Math.min(targetX, viewport.worldWidth - halfW));
                targetY = Math.max(halfH, Math.min(targetY, viewport.worldHeight - halfH));
              }
            }
            
            // Check if any word is within the current time range
            const anyActive = allWords.length > 0 && currentTimeMs >= allWords[0].start && currentTimeMs <= allWords[allWords.length - 1].end;
            
            if (anyActive) {
              container.x = targetX;
              container.y = targetY;
              container.visible = true;
            } else {
              if (container._activeWordBlurGraphics) {
                container._activeWordBlurGraphics.clear();
              }
              container._activeWordBlurTrail = [];
              container._lastActiveWordBlurKey = null;
              container._lastActiveWordBlurAnchor = null;
              container._lastActiveWordBlurTickMs = null;
              container._activeWordBlurRenderedAnchor = null;
              container.visible = false;
            }
            
          } else {
            // ======== STANDARD MODE: Phrase-by-phrase ========
            const captionPhrases = element.componentData.captions.phrases || [];
            const isTypewriterMode = isTypewriterCaptionStyle(captionStyle);
            let activeCaption = captionPhrases.find(
              c => currentTimeMs >= c.start && currentTimeMs <= c.end
            );

            // Typewriter cursor tail: keep the latest phrase visible for 2 seconds
            // after its final character appears, unless the next phrase has started.
            if (!activeCaption && isTypewriterMode) {
              for (let phraseIndex = captionPhrases.length - 1; phraseIndex >= 0; phraseIndex -= 1) {
                const phrase = captionPhrases[phraseIndex];
                const nextPhrase = captionPhrases[phraseIndex + 1];
                const withinCursorTail = currentTimeMs > phrase.end && currentTimeMs <= phrase.end + 2000;
                const nextPhraseNotStarted = !nextPhrase || currentTimeMs < nextPhrase.start;
                if (withinCursorTail && nextPhraseNotStarted) {
                  activeCaption = phrase;
                  break;
                }
              }
            }

            if (activeCaption) {
              const fullText = activeCaption.text || '';
              let nextText = fullText;

              if (isTypewriterMode) {
                const isCursorTail = currentTimeMs > activeCaption.end && currentTimeMs <= activeCaption.end + 2000;
                const cursorGlyph = captionStyle.typewriterCursor === 'block' ? '\u2589' : '|';

                if (isCursorTail) {
                  nextText = `${fullText}${cursorGlyph}`;
                } else {
                  const phraseDuration = Math.max(1, activeCaption.end - activeCaption.start);
                  const elapsed = Math.max(0, Math.min(phraseDuration, currentTimeMs - activeCaption.start));
                  const progress = Math.max(0, Math.min(1, elapsed / phraseDuration));
                  const typedCharacterCount = Math.max(
                    0,
                    Math.min(fullText.length, Math.ceil(fullText.length * progress))
                  );
                  const typedText = fullText.slice(0, typedCharacterCount);
                  nextText = `${typedText}${cursorGlyph}`;
                }
              }

              let isTextChanged = element.captionSprite.text !== nextText;
              if (isTextChanged) {
                element.captionSprite.text = nextText;
                
                const animationType = captionStyle.animation;
                if (!isTypewriterMode && animationType) {
                  gsap.killTweensOf(element.captionSprite);
                  gsap.killTweensOf(element.captionSprite.scale);
                  element.captionSprite.alpha = 1;
                  if (element.captionSprite.scale) element.captionSprite.scale.set(1, 1);
                }
              }

              // Strictly enforce boundary
              if (viewport && element.captionSprite) {
                if (element.captionSprite.style) {
                  // Temporarily disabled max-lines/wrap optimization controls.
                  const wrapWidth = viewport.worldWidth * 0.8;
                  if (!element.captionSprite.style.wordWrap) {
                    element.captionSprite.style.wordWrap = true;
                  }
                  element.captionSprite.style.wordWrapWidth = wrapWidth;
                }
                const halfW = element.captionSprite.width / 2;
                const halfH = element.captionSprite.height / 2;
                targetX = Math.max(halfW, Math.min(targetX, viewport.worldWidth - halfW));
                targetY = Math.max(halfH, Math.min(targetY, viewport.worldHeight - halfH));
              }

              if (isTextChanged) {
                element.captionSprite.x = targetX;
                element.captionSprite.y = targetY;
                const animationType = captionStyle.animation;
                if (!isTypewriterMode && animationType && ANIMATION_FUNCTIONS[animationType]) {
                  ANIMATION_FUNCTIONS[animationType](element.captionSprite, true, targetX, targetY);
                }
              } else {
                if (!captionStyle.animation || isTypewriterMode) {
                  element.captionSprite.x = targetX;
                  element.captionSprite.y = targetY;
                }
              }

              const typewriterBgColor = isTypewriterMode
                ? captionStyle.typewriterBackgroundColor
                : null;
              const shouldRenderTypewriterBg = Boolean(typewriterBgColor && element.captionSprite.style);

              if (shouldRenderTypewriterBg) {
                const spriteParent = element.captionSprite.parent;
                if (!element.captionSprite._captionBgGraphics && spriteParent) {
                  element.captionSprite._captionBgGraphics = new PIXI.Graphics();
                  const textIndex = spriteParent.getChildIndex(element.captionSprite);
                  spriteParent.addChildAt(element.captionSprite._captionBgGraphics, Math.max(0, textIndex));
                } else if (
                  element.captionSprite._captionBgGraphics &&
                  spriteParent &&
                  element.captionSprite._captionBgGraphics.parent !== spriteParent
                ) {
                  const textIndex = spriteParent.getChildIndex(element.captionSprite);
                  spriteParent.addChildAt(element.captionSprite._captionBgGraphics, Math.max(0, textIndex));
                }

                if (element.captionSprite._captionBgGraphics) {
                  const renderedText = element.captionSprite.text || '';
                  const cursorGlyph = captionStyle.typewriterCursor === 'block' ? '\u2589' : '|';
                  const textWithoutCursor = renderedText.endsWith(cursorGlyph)
                    ? renderedText.slice(0, renderedText.length - cursorGlyph.length)
                    : renderedText;

                  const renderedMetrics = PIXI.CanvasTextMetrics.measureText(
                    renderedText,
                    element.captionSprite.style
                  );
                  const textMetrics = PIXI.CanvasTextMetrics.measureText(
                    textWithoutCursor,
                    element.captionSprite.style
                  );
                  const renderedLines = Array.isArray(renderedMetrics.lines) ? renderedMetrics.lines : [];
                  const lines = Array.isArray(textMetrics.lines) ? textMetrics.lines : [];
                  const lineCount = Math.max(1, lines.length);
                  const lineHeight = Number(textMetrics.lineHeight)
                    || (textMetrics.height > 0 ? textMetrics.height / lineCount : (element.captionSprite.style.fontSize || 16));
                  const renderedLineWidths = (
                    Array.isArray(renderedMetrics.lineWidths) && renderedMetrics.lineWidths.length === renderedLines.length
                  )
                    ? renderedMetrics.lineWidths
                    : renderedLines.map((lineText) => {
                      const lineMetrics = PIXI.CanvasTextMetrics.measureText(lineText, element.captionSprite.style);
                      return lineMetrics.width || 0;
                    });
                  const lineWidths = (
                    Array.isArray(textMetrics.lineWidths) && textMetrics.lineWidths.length === lines.length
                  )
                    ? textMetrics.lineWidths
                    : lines.map((lineText) => {
                      const lineMetrics = PIXI.CanvasTextMetrics.measureText(lineText, element.captionSprite.style);
                      return lineMetrics.width || 0;
                    });
                  const padX = Number.isFinite(Number(captionStyle.typewriterBackgroundPaddingX))
                    ? Math.max(0, Number(captionStyle.typewriterBackgroundPaddingX))
                    : 8;
                  const padY = Number.isFinite(Number(captionStyle.typewriterBackgroundPaddingY))
                    ? Math.max(0, Number(captionStyle.typewriterBackgroundPaddingY))
                    : 4;
                  const radius = Number.isFinite(Number(captionStyle.typewriterBackgroundRadius))
                    ? Math.max(0, Number(captionStyle.typewriterBackgroundRadius))
                    : 4;
                  const bgKey = [
                    typewriterBgColor,
                    renderedText,
                    lines.join('\n'),
                    lineWidths.map((w) => Number(w).toFixed(2)).join(','),
                    lineHeight.toFixed(2),
                    padX,
                    padY,
                    radius,
                    targetX.toFixed(2),
                    targetY.toFixed(2),
                  ].join('|');

                  if (element.captionSprite._lastCaptionBgKey !== bgKey) {
                    element.captionSprite._lastCaptionBgKey = bgKey;
                    element.captionSprite._captionBgGraphics.clear();
                    if (lines.length > 0) {
                      const textTop = targetY - ((lineHeight * lineCount) / 2);
                      // Keep each line-box separate while still fully covering glyphs.
                      const lineGap = Math.max(1, Math.round(lineHeight * 0.12));
                      const verticalPad = Math.max(1, Math.min(padY, Math.round(lineHeight * 0.16)));
                      const lineBoxHeight = Math.max(1, (lineHeight - lineGap) + (verticalPad * 2));
                      const lineRadius = Math.min(radius, lineBoxHeight / 2);
                      const edgeBoost = Math.max(
                        1,
                        Math.round((Number(element.captionSprite.style?.fontSize) || 16) * 0.04)
                      );

                      lines.forEach((_, lineIndex) => {
                        const lineWidth = Math.max(0, Number(lineWidths[lineIndex] || 0));
                        if (lineWidth <= 0.5) return;

                        const lineY = textTop + (lineIndex * lineHeight);
                        // Anchor to the rendered line bounds (includes cursor influence) so
                        // the left edge stays fully covered at the beginning of typing.
                        const renderedLineWidth = Math.max(
                          lineWidth,
                          Number(renderedLineWidths[lineIndex] || lineWidth)
                        );
                        const lineLeft = targetX - (renderedLineWidth / 2);
                        element.captionSprite._captionBgGraphics.roundRect(
                          lineLeft - padX - (edgeBoost / 2),
                          lineY + (lineGap / 2) - verticalPad,
                          lineWidth + (padX * 2) + edgeBoost,
                          lineBoxHeight,
                          lineRadius
                        );
                      });
                      element.captionSprite._captionBgGraphics.fill(parseHexColor(typewriterBgColor, 0x000000), 1);
                    }
                  }
                }
              } else if (element.captionSprite._captionBgGraphics) {
                element.captionSprite._captionBgGraphics.clear();
                element.captionSprite._lastCaptionBgKey = null;
              }

              element.captionSprite.visible = true;
            } else {
              if (element.captionSprite.text !== '') {
                element.captionSprite.text = '';
                gsap.killTweensOf(element.captionSprite);
              }
              if (element.captionSprite._captionBgGraphics) {
                element.captionSprite._captionBgGraphics.clear();
                element.captionSprite._lastCaptionBgKey = null;
              }
              element.captionSprite.visible = false;
            }
          }
        } else {
          // Hide all other caption sprites
          if (!element.captionSprite._isWordContainer && element.captionSprite.text !== '') {
            element.captionSprite.text = '';
            gsap.killTweensOf(element.captionSprite);
          }
          if (element.captionSprite._captionBgGraphics) {
            element.captionSprite._captionBgGraphics.clear();
            element.captionSprite._lastCaptionBgKey = null;
          }
          if (element.captionSprite._activeWordBlurGraphics) {
            element.captionSprite._activeWordBlurGraphics.clear();
          }
          element.captionSprite._activeWordBlurTrail = [];
          element.captionSprite._lastActiveWordBlurKey = null;
          element.captionSprite._lastActiveWordBlurAnchor = null;
          element.captionSprite._lastActiveWordBlurTickMs = null;
          element.captionSprite._activeWordBlurRenderedAnchor = null;
          element.captionSprite.visible = false;
        }
      });
      // -------------------------------------

    },

    // Timeline helper methods
    getElementsAtTime: (time) => {
      const state = get();
      return state.canvasElements.filter((element) => {
        const timeline = element.timeline;
        if (!timeline) return false;
        return time >= timeline.inPoint && time <= timeline.outPoint;
      });
    },

    // History management
    undo: async (spriteCreators) => {
      const state = get();
      if (state.historyIndex <= 0 || state.isRestoringHistory) return;

      // Stop playback before undoing to avoid audio conflicts
      if (state.isPlaying) {
        state.stopPlaybackLoop();
        console.log("Paused playback before undo");
      }

      set((state) => ({
        historyIndex: state.historyIndex - 1,
        isRestoringHistory: true,
      }));

      await get().restoreFromHistory(spriteCreators);

      // Reset flag after a short delay to prevent rapid operations
      setTimeout(() => {
        set({ isRestoringHistory: false });
      }, 100);
    },

    redo: async (spriteCreators) => {
      const state = get();
      if (
        state.historyIndex >= state.history.length - 1 ||
        state.isRestoringHistory
      )
        return;

      // Stop playback before redoing to avoid audio conflicts
      if (state.isPlaying) {
        state.stopPlaybackLoop();
        console.log("Paused playback before redo");
      }

      set((state) => ({
        historyIndex: state.historyIndex + 1,
        isRestoringHistory: true,
      }));

      await get().restoreFromHistory(spriteCreators);

      // Reset flag after a short delay to prevent rapid operations
      setTimeout(() => {
        set({ isRestoringHistory: false });
      }, 100);
    },

    restoreFromHistory: async (spriteCreators) => {
      const state = get();
      const snapshot = state.history[state.historyIndex];

      if (!snapshot || !state.viewport) return;

      const snapshotCanvasColor = normalizeHexColor(
        snapshot.canvasBackgroundColor,
        DEFAULT_CANVAS_BACKGROUND_COLOR
      );
      if (state.canvasBackgroundColor !== snapshotCanvasColor) {
        set({ canvasBackgroundColor: snapshotCanvasColor });
      }

      const visualContainer = state.viewport.visualContainer || state.viewport;
      const interactiveContainer = state.viewport.interactiveContainer || state.viewport;

      const scene = visualContainer?.children?.[0];
      if (scene instanceof PIXI.Graphics) {
        scene.clear();
        scene.rect(0, 0, state.canvasSize.width, state.canvasSize.height);
        scene.fill(parseHexColor(snapshotCanvasColor, 0x000000));
        scene.stroke({ width: 1, color: 0x999999 });
      }

      // Use the persistent job states Map
      const persistentJobStates = state.activeJobStates;

      // Build a map of current sprites by element ID for reuse
      const currentSpritesMap = new Map();
      state.canvasElements.forEach(element => {
        if (element.pixiSprite) {
          currentSpritesMap.set(element.id, {
            sprite: element.pixiSprite,
            interactiveArea: element.pixiSprite.interactiveArea,
            element: element
          });
        }
      });

      // Build a set of element IDs that should exist after restore
      const targetElementIds = new Set(snapshot.canvasElements.map(el => el.id));

      // Hide sprites that shouldn't be visible (deleted elements)
      currentSpritesMap.forEach((spriteData, elementId) => {
        if (!targetElementIds.has(elementId)) {
          // Just hide instead of destroying
          spriteData.sprite.visible = false;
          if (spriteData.interactiveArea) {
            spriteData.interactiveArea.visible = false;
          }
          // Store in pool for potential reuse
          spritePoolManager.storeSprite(
            elementId,
            spriteData.sprite,
            spriteData.interactiveArea,
            spriteData.element
          );
        }
      });

      // Restore elements - reuse existing sprites where possible
      const restoredElements = [];

      for (const snapElement of snapshot.canvasElements) {
        const existingSpriteData = currentSpritesMap.get(snapElement.id);

        if (existingSpriteData) {
          // Check if the media URL has changed (e.g., undo/redo of media replacement)
          const currentUrl = existingSpriteData.element?.componentData?.url;
          const snapshotUrl = snapElement.componentData?.url;
          const mediaChanged = currentUrl && snapshotUrl && currentUrl !== snapshotUrl;

          if (mediaChanged) {
            // Media was replaced - need to destroy current sprite and create new one
            const oldSprite = existingSpriteData.sprite;
            const oldInteractiveArea = existingSpriteData.interactiveArea;

            // Remove old sprite from containers
            if (oldInteractiveArea && oldInteractiveArea.parent) {
              oldInteractiveArea.parent.removeChild(oldInteractiveArea);
            }
            if (oldSprite && oldSprite.parent) {
              oldSprite.parent.removeChild(oldSprite);
            }

            // Cleanup and destroy old sprite
            if (oldSprite && !oldSprite.destroyed) {
              if (oldSprite.videoElement) {
                oldSprite.videoElement.pause();
              }
              if (oldSprite._cleanupVideo) {
                oldSprite._cleanupVideo();
              }
              if (oldSprite._cleanupAudio) {
                oldSprite._cleanupAudio();
              }
              if (oldSprite._cleanupAnimation) {
                oldSprite._cleanupAnimation();
              }
              oldSprite.destroy({ children: true });
            }

            // Create new sprite from snapshot componentData (falls through to sprite creation below)
            // by NOT pushing to restoredElements here and letting the else branch handle it
            if (snapElement.componentData && spriteCreators) {
              let createVideoSprite, createImageSprite, createAudioSprite, createGifSprite, createAiSprite;
              if (typeof spriteCreators === "function") {
                createVideoSprite = spriteCreators;
              } else if (spriteCreators && typeof spriteCreators === "object") {
                createVideoSprite = spriteCreators.createVideoSprite;
                createImageSprite = spriteCreators.createImageSprite;
                createAudioSprite = spriteCreators.createAudioSprite;
                createGifSprite = spriteCreators.createGifSprite;
                createAiSprite = spriteCreators.createAiSprite;
              }

              try {
                let sprite = null;
                if (snapElement.type === "ai" && createAiSprite) {
                  sprite = await createAiSprite(snapElement.componentData, snapElement.id, snapElement.transform?.x || 0, snapElement.transform?.y || 0);
                } else if (snapElement.type === "gif" && createGifSprite) {
                  sprite = await createGifSprite(snapElement.componentData, snapElement.id, snapElement.transform?.x || 0, snapElement.transform?.y || 0);
                } else if (snapElement.type === "image" && createImageSprite) {
                  sprite = await createImageSprite(snapElement.componentData, snapElement.id, snapElement.transform?.x || 0, snapElement.transform?.y || 0);
                } else if (snapElement.type === "audio" && createAudioSprite) {
                  sprite = await createAudioSprite(snapElement.componentData, snapElement.id, snapElement.transform?.x || 0, snapElement.transform?.y || 0);
                } else if (createVideoSprite) {
                  const agComponentSrc = snapElement.componentData?.currSrc || snapElement.componentData?.url || null;
                  sprite = await createVideoSprite(snapElement.componentData, snapElement.id, snapElement.transform?.x || 0, snapElement.transform?.y || 0, agComponentSrc);
                }

                if (sprite) {
                  sprite.elementId = snapElement.id;
                  sprite.zIndex = snapElement.zIndex || 0;

                  if (snapElement.transform) {
                    sprite.x = snapElement.transform.x;
                    sprite.y = snapElement.transform.y;
                    sprite.rotation = snapElement.transform.rotation;
                    if (sprite.scale && sprite.scale.set) {
                      sprite.scale.set(snapElement.transform.scaleX, snapElement.transform.scaleY);
                    }
                    if (sprite.anchor && sprite.anchor.set) {
                      sprite.anchor.set(snapElement.transform.anchorX, snapElement.transform.anchorY);
                    }
                    sprite.visible = snapElement.transform.visible;
                    sprite.alpha = snapElement.transform.alpha;

                    if (sprite.interactiveArea) {
                      sprite.interactiveArea.elementId = snapElement.id;
                      sprite.interactiveArea.x = snapElement.transform.x;
                      sprite.interactiveArea.y = snapElement.transform.y;
                      sprite.interactiveArea.rotation = snapElement.transform.rotation;
                      if (sprite.interactiveArea.scale && sprite.interactiveArea.scale.set) {
                        sprite.interactiveArea.scale.set(snapElement.transform.scaleX, snapElement.transform.scaleY);
                      }
                      sprite.interactiveArea.zIndex = snapElement.zIndex || 0;
                    }
                  }

                  visualContainer.addChild(sprite);
                  if (sprite.interactiveArea) {
                    interactiveContainer.addChild(sprite.interactiveArea);
                  }

                  const preservedJobState = persistentJobStates.get(snapElement.id);
                  const elementToRestore = { ...snapElement, pixiSprite: sprite };
                  if (preservedJobState) {
                    elementToRestore.jobStatus = preservedJobState.jobStatus;
                    elementToRestore.jobProgress = preservedJobState.jobProgress;
                  } else {
                    elementToRestore.jobStatus = '';
                    elementToRestore.jobProgress = '';
                  }
                  restoredElements.push(elementToRestore);
                } else {
                  restoredElements.push({ ...snapElement, pixiSprite: null });
                }
              } catch (error) {
                console.warn("Failed to recreate sprite for media undo/redo:", error);
                restoredElements.push(snapElement);
              }
            } else {
              restoredElements.push(snapElement);
            }

          } else {
            // REUSE EXISTING SPRITE - just update properties (no media change)
            const sprite = existingSpriteData.sprite;
            const interactiveArea = existingSpriteData.interactiveArea;

            // Update transform from snapshot
            if (snapElement.transform) {
              sprite.x = snapElement.transform.x;
              sprite.y = snapElement.transform.y;
              sprite.rotation = snapElement.transform.rotation;

              // Handle scale - check if sprite has scale property (placeholder Graphics might not)
              if (sprite.scale && sprite.scale.set) {
                sprite.scale.set(
                  snapElement.transform.scaleX,
                  snapElement.transform.scaleY
                );
              }

              // Handle anchor - check if sprite has anchor property (Graphics objects don't have anchor)
              if (sprite.anchor && sprite.anchor.set) {
                sprite.anchor.set(
                  snapElement.transform.anchorX,
                  snapElement.transform.anchorY
                );
              }

              sprite.visible = snapElement.transform.visible;
              sprite.alpha = snapElement.transform.alpha;
              sprite.zIndex = snapElement.zIndex || 0;

              // Update interactive area
              if (interactiveArea) {
                interactiveArea.x = snapElement.transform.x;
                interactiveArea.y = snapElement.transform.y;
                interactiveArea.rotation = snapElement.transform.rotation;

                // Handle scale for interactive area
                if (interactiveArea.scale && interactiveArea.scale.set) {
                  interactiveArea.scale.set(
                    snapElement.transform.scaleX,
                    snapElement.transform.scaleY
                  );
                }

                interactiveArea.visible = snapElement.transform.visible;
                interactiveArea.zIndex = snapElement.zIndex || 0;
              }
            }

            // Update muted state for videos
            if (sprite.videoElement && snapElement.muted !== undefined) {
              sprite.videoElement.muted = snapElement.muted;
            }

            // Ensure sprite is visible
            sprite.visible = true;
            if (interactiveArea) {
              interactiveArea.visible = true;
            }

            // Create restored element with existing sprite
            const preservedJobState = persistentJobStates.get(snapElement.id);
            const elementToRestore = {
              ...snapElement,
              pixiSprite: sprite
            };

            if (preservedJobState) {
              elementToRestore.jobStatus = preservedJobState.jobStatus;
              elementToRestore.jobProgress = preservedJobState.jobProgress;
            } else {
              elementToRestore.jobStatus = '';
              elementToRestore.jobProgress = '';
            }

            restoredElements.push(elementToRestore);
          } // end else (no media change)

        } else {
          // Element doesn't exist - check pool first
          const pooledData = spritePoolManager.getSprite(snapElement.id);

          if (pooledData) {
            // REUSE FROM POOL
            const sprite = pooledData.sprite;
            const interactiveArea = pooledData.interactiveArea;

            // Validate sprite before using
            if (!sprite) {
              console.warn(`Pooled sprite is null for element ${snapElement.id}, skipping`);
              continue;
            }

            // Update properties from snapshot
            if (snapElement.transform) {
              sprite.x = snapElement.transform.x;
              sprite.y = snapElement.transform.y;
              sprite.rotation = snapElement.transform.rotation;

              // Handle scale - check if sprite has scale property (placeholder Graphics might not)
              if (sprite.scale && sprite.scale.set) {
                sprite.scale.set(
                  snapElement.transform.scaleX,
                  snapElement.transform.scaleY
                );
              }

              // Handle anchor - check if sprite has anchor property (Graphics objects don't have anchor)
              if (sprite.anchor && sprite.anchor.set) {
                sprite.anchor.set(
                  snapElement.transform.anchorX,
                  snapElement.transform.anchorY
                );
              }

              sprite.alpha = snapElement.transform.alpha;
              sprite.zIndex = snapElement.zIndex || 0;

              if (interactiveArea) {
                interactiveArea.x = snapElement.transform.x;
                interactiveArea.y = snapElement.transform.y;
                interactiveArea.rotation = snapElement.transform.rotation;

                // Handle scale for interactive area
                if (interactiveArea.scale && interactiveArea.scale.set) {
                  interactiveArea.scale.set(
                    snapElement.transform.scaleX,
                    snapElement.transform.scaleY
                  );
                }

                interactiveArea.zIndex = snapElement.zIndex || 0;
              }
            }

            // Re-add to containers if needed
            if (!sprite.parent) {
              visualContainer.addChild(sprite);
            }
            if (interactiveArea && !interactiveArea.parent) {
              interactiveContainer.addChild(interactiveArea);
            }

            const preservedJobState = persistentJobStates.get(snapElement.id);
            const elementToRestore = {
              ...snapElement,
              pixiSprite: sprite
            };

            if (preservedJobState) {
              elementToRestore.jobStatus = preservedJobState.jobStatus;
              elementToRestore.jobProgress = preservedJobState.jobProgress;
            } else {
              elementToRestore.jobStatus = '';
              elementToRestore.jobProgress = '';
            }

            restoredElements.push(elementToRestore);

          } else if (snapElement.componentData && spriteCreators) {
            // Only create new sprite if absolutely necessary (not in pool)
            // This should be rare after initial creation

            let createVideoSprite, createImageSprite, createAudioSprite, createGifSprite, createAiSprite;
            if (typeof spriteCreators === "function") {
              createVideoSprite = spriteCreators;
            } else if (spriteCreators && typeof spriteCreators === "object") {
              createVideoSprite = spriteCreators.createVideoSprite;
              createImageSprite = spriteCreators.createImageSprite;
              createAudioSprite = spriteCreators.createAudioSprite;
              createGifSprite = spriteCreators.createGifSprite;
              createAiSprite = spriteCreators.createAiSprite;
            }

            try {
              let sprite = null;

              if (snapElement.type === "ai" && createAiSprite) {
                sprite = await createAiSprite(
                  snapElement.componentData,
                  snapElement.id,
                  snapElement.x || 0,
                  snapElement.y || 0
                );
              } else if (snapElement.type === "gif" && createGifSprite) {
                sprite = await createGifSprite(
                  snapElement.componentData,
                  snapElement.id,
                  snapElement.x || 0,
                  snapElement.y || 0
                );
              } else if (snapElement.type === "image" && createImageSprite) {
                sprite = await createImageSprite(
                  snapElement.componentData,
                  snapElement.id,
                  snapElement.x || 0,
                  snapElement.y || 0
                );
              } else if (snapElement.type === "audio" && createAudioSprite) {
                sprite = await createAudioSprite(
                  snapElement.componentData,
                  snapElement.id,
                  snapElement.x || 0,
                  snapElement.y || 0
                );
              } else if (createVideoSprite) {
                const agComponentSrc = snapElement.componentData?.currSrc ||
                  snapElement.componentData?.url ||
                  snapElement.componentData?.preview || null;
                sprite = await createVideoSprite(
                  snapElement.componentData,
                  snapElement.id,
                  snapElement.x || 0,
                  snapElement.y || 0,
                  agComponentSrc
                );
              }

              // Check if sprite creation was successful
              if (!sprite) {
                console.warn(`Failed to create sprite for element ${snapElement.name} (type: ${snapElement.type}), skipping`);
                // Add element without sprite to maintain state consistency
                restoredElements.push({
                  ...snapElement,
                  pixiSprite: null
                });
                continue;
              }

              if (sprite) {
                sprite.elementId = snapElement.id;
                sprite.zIndex = snapElement.zIndex || 0;

                if (snapElement.transform) {
                  sprite.x = snapElement.transform.x;
                  sprite.y = snapElement.transform.y;
                  sprite.rotation = snapElement.transform.rotation;

                  // Handle scale - check if sprite has scale property
                  if (sprite.scale && sprite.scale.set) {
                    sprite.scale.set(
                      snapElement.transform.scaleX,
                      snapElement.transform.scaleY
                    );
                  }

                  // Handle anchor - check if sprite has anchor property
                  if (sprite.anchor && sprite.anchor.set) {
                    sprite.anchor.set(
                      snapElement.transform.anchorX,
                      snapElement.transform.anchorY
                    );
                  }

                  sprite.visible = snapElement.transform.visible;
                  sprite.alpha = snapElement.transform.alpha;

                  if (sprite.interactiveArea) {
                    sprite.interactiveArea.elementId = snapElement.id;
                    sprite.interactiveArea.x = snapElement.transform.x;
                    sprite.interactiveArea.y = snapElement.transform.y;
                    sprite.interactiveArea.rotation = snapElement.transform.rotation;

                    // Handle scale for interactive area
                    if (sprite.interactiveArea.scale && sprite.interactiveArea.scale.set) {
                      sprite.interactiveArea.scale.set(
                        snapElement.transform.scaleX,
                        snapElement.transform.scaleY
                      );
                    }

                    sprite.interactiveArea.zIndex = snapElement.zIndex || 0;
                  }
                }

                if (sprite.videoElement && snapElement.muted !== undefined) {
                  sprite.videoElement.muted = snapElement.muted;
                }

                visualContainer.addChild(sprite);
                if (sprite.interactiveArea) {
                  interactiveContainer.addChild(sprite.interactiveArea);
                }

                const preservedJobState = persistentJobStates.get(snapElement.id);
                const elementToRestore = {
                  ...snapElement,
                  pixiSprite: sprite
                };

                if (preservedJobState) {
                  elementToRestore.jobStatus = preservedJobState.jobStatus;
                  elementToRestore.jobProgress = preservedJobState.jobProgress;
                } else {
                  elementToRestore.jobStatus = '';
                  elementToRestore.jobProgress = '';
                }

                // Restore Caption Sprite if captions exist
                if (snapElement.componentData?.captions?.phrases?.length > 0) {
                  const captionData = snapElement.componentData.captions;

                  if (state.viewport) {
                    const captionStyle = sanitizeCaptionStyle(captionData.style || {});
                    const captionFontFamily = captionStyle.fontFamily || 'Arial';
                    await ensureFontLoaded(captionFontFamily);

                    const needsWordContainer = shouldUseWordContainer(captionStyle);
                    let captionSprite;

                    if (needsWordContainer) {
                      // Word container mode
                      captionSprite = new PIXI.Container();
                      captionSprite._isWordContainer = true;
                      captionSprite._highlightColor = captionStyle.highlightColor || null;
                      captionSprite._baseColor = captionStyle.color || '#ffffff';
                      captionSprite._captionStyle = captionStyle;
                      captionSprite._displayMode = captionStyle.displayMode || 'karaoke';
                      captionSprite._highlightBg = captionStyle.highlightBg || null;
                      captionSprite._wordAnimation = captionStyle.wordAnimation ?? null;
                      captionSprite._pastPhraseColor = captionStyle.pastPhraseColor || '#ffffff';
                      captionSprite._activeWordBlurGraphics = null;
                      captionSprite._activeWordBlurTrail = [];
                      captionSprite._lastActiveWordBlurKey = null;
                      captionSprite._lastActiveWordBlurAnchor = null;
                      captionSprite._lastActiveWordBlurTickMs = null;
                      captionSprite._activeWordBlurRenderedAnchor = null;
                    } else {
                      // Standard PIXI.Text mode
                      const style = new PIXI.TextStyle({
                        fontFamily: captionFontFamily,
                        fontSize: captionStyle.fontSize || 36,
                        fill: captionStyle.color || '#ffffff',
                        align: 'center',
                        wordWrap: true,
                        wordWrapWidth: 1600,
                        ...(captionStyle.stroke ? {
                          stroke: captionStyle.stroke,
                          ...(captionStyle.strokeThickness !== undefined ? { strokeThickness: captionStyle.strokeThickness } : {}),
                          ...(captionStyle.lineJoin ? { lineJoin: captionStyle.lineJoin } : {}),
                        } : {}),
                        // Temporarily disabled drop-shadow styling.
                      });
                      captionSprite = new PIXI.Text({
                        text: captionData.phrases[0].text,
                        style
                      });
                      captionSprite.anchor.set(0.5);
                    }

                    captionSprite.x = captionStyle.x ?? 960;
                    captionSprite.y = captionStyle.y ?? 900;
                    captionSprite.eventMode = 'static';

                    state.viewport.addChild(captionSprite);
                    elementToRestore.captionSprite = captionSprite;
                  }
                }

                restoredElements.push(elementToRestore);
              }
            } catch (error) {
              console.warn("Failed to create sprite:", error);
              restoredElements.push(snapElement);
            }
          } else {
            restoredElements.push(snapElement);
          }
        }
      }

      // Restore selectedElements array based on stored IDs
      let restoredSelectedElements = [];
      if (snapshot.selectedElementIds && snapshot.selectedElementIds.length > 0) {
        restoredSelectedElements = restoredElements.filter((el) =>
          snapshot.selectedElementIds.includes(el.id)
        );
      }

      set(() => ({
        canvasElements: restoredElements,
        selectedElement: snapshot.selectedElement,
        selectedElements: restoredSelectedElements,
      }));

      // Sync z-indices and force sort
      syncPixiSprites(restoredElements);

      if (visualContainer.sortableChildren) {
        visualContainer.sortDirty = true;
      }
      if (interactiveContainer.sortableChildren) {
        interactiveContainer.sortDirty = true;
      }

      // Update sprites visibility based on current time
      state.updateSpritesForTime(state.currentTime);

      // Update duration based on restored elements
      state.updateDurationFromElements();
    },

    // Utility methods
    canUndo: () => get().historyIndex > 0,
    canRedo: () => get().historyIndex < get().history.length - 1,

    getHistoryInfo: () => ({
      currentIndex: get().historyIndex,
      totalStates: get().history.length,
      maxSize: MAX_HISTORY_SIZE,
    }),

    getSortedElements: () => getSortedElements(get().canvasElements),
    getElementLayerPosition: (elementId) =>
      getElementLayerPosition(get().canvasElements, elementId),
    canMoveForward: (elementId) =>
      canMoveForward(get().canvasElements, elementId),
    canMoveBackward: (elementId) =>
      canMoveBackward(get().canvasElements, elementId),

    // Copy/Paste
    copyElements: () => {
      const state = get();
      const elementsToCopy = state.selectedElements.length > 0
        ? state.selectedElements
        : (state.selectedElement ? [state.selectedElement] : []);

      if (elementsToCopy.length === 0) return;

      const clipboard = elementsToCopy.map(el => {
        const sprite = el.pixiSprite;
        return {
          type: el.type,
          name: el.name,
          componentData: JSON.parse(JSON.stringify(el.componentData)),
          x: sprite ? sprite.x : (el.x || 0),
          y: sprite ? sprite.y : (el.y || 0),
          scaleX: sprite?.scale?.x || el.scaleX || 1,
          scaleY: sprite?.scale?.y || el.scaleY || 1,
          rotation: sprite?.rotation || el.rotation || 0,
          alpha: el.alpha !== undefined ? el.alpha : 1,
          volume: el.volume !== undefined ? el.volume : 1,
          muted: el.muted || false,
          timeline: el.timeline ? { ...el.timeline } : null,
          mediaCropTrim: el.mediaCropTrim ? JSON.parse(JSON.stringify(el.mediaCropTrim)) : undefined,
          playbackMode: el.playbackMode || 'loop',
        };
      });

      set({ clipboard });
      console.log(`ðŸ“‹ Copied ${clipboard.length} element(s) to clipboard`);
    },

    getClipboard: () => get().clipboard,

    pasteElements: async (addComponentToCanvas) => {
      const clipboardItems = get().clipboard;
      if (!clipboardItems || clipboardItems.length === 0 || !addComponentToCanvas) return;

      console.log(`ðŸ“‹ Pasting ${clipboardItems.length} element(s)`);
      for (const item of clipboardItems) {
        // Deep-clone componentData so the new element gets its own copy
        // For AG Components, this ensures createVideoSprite treats it as a first-time add
        // and copies the file to a new R2 key
        const clonedComponentData = JSON.parse(JSON.stringify(item.componentData));

        // For AI sprites, clear the spriteId so a new independent one is generated.
        // Without this, the pasted copy shares the same spriteId and stays linked
        // to the original (same job, same media updates).
        if (clonedComponentData.type === 'ai' && clonedComponentData.spriteId) {
          delete clonedComponentData.spriteId;
        }

        const position = {
          x: (item.x || 0) + 20,
          y: (item.y || 0) + 20,
        };

        const layerProperties = {
          scaleX: item.scaleX,
          scaleY: item.scaleY,
          rotation: item.rotation,
          alpha: item.alpha,
          volume: item.volume,
          muted: item.muted,
          timeline: item.timeline ? { ...item.timeline } : undefined,
          playbackMode: item.playbackMode,
          x: position.x,
          y: position.y,
        };

        await addComponentToCanvas(clonedComponentData, position, layerProperties);
      }
    },

    // Clear history
    clearHistory: () =>
      set((state) => ({
        history: [
          {
            canvasElements: state.canvasElements.map(createElementSnapshot),
            selectedElement: createElementSnapshot(state.selectedElement),
            canvasBackgroundColor: state.canvasBackgroundColor,
            timestamp: Date.now(),
          },
        ],
        historyIndex: 0,
      })),

    // Calculate and update duration based on highest outpoint
    updateDurationFromElements: () => {
      const state = get();
      const FPS = 25;

      if (state.canvasElements.length === 0) {
        // No elements, use default duration
        set({ duration: 10 });
        return;
      }

      // Find the highest outpoint among all elements
      let maxOutpoint = 0;
      state.canvasElements.forEach((element) => {
        if (element.timeline && element.timeline.outPoint) {
          maxOutpoint = Math.max(maxOutpoint, element.timeline.outPoint);
        }
      });

      // Convert frames to seconds and add padding
      const maxDurationInSeconds = maxOutpoint / FPS + state.durationPadding;

      // Update duration if it changed
      if (state.duration !== maxDurationInSeconds) {
        set({ duration: maxDurationInSeconds });
        console.log(
          `Updated canvas duration to ${maxDurationInSeconds}s (max outpoint: ${maxOutpoint} frames + ${state.durationPadding}s padding)`
        );
      }
    },
  }))
);

export default useCanvasStore;
